"use client";

import Image from "next/image";
import { trackAudio, workAudioUrl } from "@/lib/track-audio";
import { AlbumCreditSheet } from "@/components/credits/album-credit-sheet";
import { PerRowPicker, PER_ROW_GRID, usePerRow } from '@/components/ui/per-row-picker';
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ShareButton } from "@/components/ui/share-button";
import { QRShareButton } from "@/components/ui/qr-share-button";
import {
  ArrowLeft,
  Music2,
  Play,
  Pause,
  Heart,
  Clock,
  Disc,
  Download,
  ListMusic,
  SkipForward,
  Volume2,
  FileText,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  Send,
  Trash2,
  Globe,
  Timer,
  AudioLines,
  Pencil,
  Plus,
  Check,
  X,
  Search,
  AlertCircle,
  LinkIcon,
  ImagePlus,
  Loader2,
  RefreshCw,
  Upload,
  ExternalLink,
  Activity,
  GripHorizontal,
  Video,
  PlusCircle,
  Clapperboard,
  Package,
  Users,
  UserRound,
  Disc3,
} from "lucide-react";
import { Breadcrumbs, artistHref, type Crumb } from "@/components/layout/breadcrumbs";
import { getCoverIndex, propagateReleaseCover } from "@/services/cover-index";
import { SyncInquiryDialog } from "@/components/sync/sync-inquiry-dialog";
import { Input } from "@/components/ui/input";
import UserSearchInput from "@/components/ui/user-search-input";
import { RoleAutocomplete } from "@/components/ui/role-autocomplete";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAdmin } from "@/hooks/use-admin";
import React, { useState, useEffect, use, useCallback, useRef } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import WebResultsSection from "@/components/web-results-section";
import { db } from "@/lib/firebase";
import { getDoc, doc, collection, getDocs, query, where, limit, orderBy, updateDoc } from "firebase/firestore";
import { storage } from "@/lib/firebase";
import { isSelfOrParent, normGenreList, pickSimilar } from "@/lib/similar-content";
import GenresTagInput from "@/components/ui/genres-tag-input";
import { updateWork } from "@/services/media";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { useMediaPlayer } from "@/contexts/media-player-context";
import { useToast } from "@/hooks/use-toast";
import SaveButton from "@/components/save-button";
import { AddToPlaylist } from "@/components/media/add-to-playlist";
import { MediaCreditsPanel } from "@/components/media/media-credits-panel";
import ClaimCreditsButton from "@/components/claim-credits-button";
import { MediaLyricsPanel } from "@/components/media/media-lyrics-panel";
import AdminToolbar from "@/components/admin/admin-toolbar";
import { updateRelease, deleteRelease, toggleReleaseAdminHidden, updateMedia, deleteMedia } from "@/services/admin";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/auth-context";
import { trackMediaEvent } from "@/services/stats";
import { MusicAlbumLD, MusicRecordingLD } from "@/components/seo/json-ld";
import { LinkedSessionBadge } from "@/components/artwork/linked-session-badge";
import { SponsorButton } from "@/components/payment/sponsor-button";
import { ThemedPaymentDialog } from "@/components/payment/themed-payment-dialog";
import { MarqueeText } from "@/components/ui/marquee-text";
import {
  subscribeTrackComments,
  addTrackComment,
  deleteTrackComment,
  addTrackCommentReaction,
  removeTrackCommentReaction,
  formatCommentTimestamp,
  type TrackComment,
} from "@/services/track-comments";
import { CommentReactions } from "@/components/ui/comment-reactions";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { ArtworkStrip } from "@/components/music/artwork-strip";
import { VaultArtworkPicker, type VaultImage } from "@/components/music/vault-artwork-picker";
import { TrackWaveform } from "@/components/media/track-waveform";
import { MusicIngredientsLabel } from "@/components/music/music-ingredients-label";
import { AiLabelInlineEditor } from "@/components/media/ai-label-inline-editor";
import { ExplicitGate } from "@/components/media/explicit-lock";
import { TiltCard, CoverExplicitSticker, CoverAiSticker } from "@/components/media/tilt-card";
import { ExplicitBadge } from "@/components/ui/explicit-badge";
import { ParentalAdvisoryLabel } from "@/components/ui/parental-advisory-label";
import { GenerativeArtistAvatar } from "@/components/media/generative-artist-avatar";
import { SlideshowImage } from "@/components/ui/slideshow-image";
import { AudioReactiveCover } from "@/components/visual/audio-reactive-cover";
import { CoverDesigner } from "@/components/media/cover-designer";
import { extractVideoId } from "@/lib/youtube";
import { MusicAnalysisPanel } from "@/components/media/music-analysis-panel";
import { ScoreTranscriber } from "@/components/media/score-transcriber";
import { ParticipantPanel } from "@/components/media/participant-panel";
import { BuyPhysicalButton } from "@/components/media/buy-physical-button";
import { useIsSwedishUser } from "@/hooks/use-swedish-user";
import { useSuppressVectorOverlay } from "@/hooks/use-suppress-vector-overlay";
import { SwishCheckoutDialog } from "@/components/payment/swish-checkout-dialog";
import { useTranslations, useLocale } from "next-intl";
import { CreditLink } from "@/components/credits/credit-link";

// ─── DownloadButton component ───────────────────────────────────────────────────
function DownloadButton({ item, tracks, iconOnly = false, openOnEvent = false, autoOpen = false }: {
  item: any; tracks: any[]; iconOnly?: boolean;
  /** The page's main button: opens on coπ's "download" (the `bb-open-download`
   *  event) and when `autoOpen` (a ?download=1 link). */
  openOnEvent?: boolean; autoOpen?: boolean;
}) {
  const tDownload = useTranslations('download');
  const tMusic = useTranslations('music');
  const { userProfile, user, loading: authLoading } = useAuth();
  const { isSuperAdmin, isAdmin } = useAdmin();
  const isSwedish = useIsSwedishUser();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [userPrice, setUserPrice] = useState<number | ''>(item.downloadMinPrice ?? 0);
  // Buyer name + anonymous choice for leaderboard
  const [buyerName, setBuyerName] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  // Swish dialog state
  const [swishOpen, setSwishOpen] = useState(false);
  const [swishAmountSEK, setSwishAmountSEK] = useState(0);
  // Tier-2 embedded themed checkout (Payment Element on our own page).
  const [themed, setThemed] = useState<{ amount: number; body: any } | null>(null);
  // Set below to the click action, so coπ / a ?download=1 link can open it.
  const openRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (!openOnEvent) return;
    const open = () => openRef.current();
    window.addEventListener('bb-open-download', open);
    return () => window.removeEventListener('bb-open-download', open);
  }, [openOnEvent]);
  const autoOpened = useRef(false);
  useEffect(() => {
    if (!autoOpen || autoOpened.current) return;
    autoOpened.current = true;
    const id = setTimeout(() => openRef.current(), 400);
    return () => clearTimeout(id);
  }, [autoOpen]);

  // Determine effective download settings
  const allowDl = item.allowDownloads || (tracks.length > 0 && tracks.some((t: any) => t.allowDownloads));
  const downloadType = item.downloadType || (tracks[0]?.downloadType || 'pwyw');
  // True Name Your Price — 0 minimum means totally free if user enters 0
  const rawMinPrice = item.downloadMinPrice ?? (tracks[0]?.downloadMinPrice ?? 0);
  const minPrice = rawMinPrice; // No forced minimum — respect artist's setting
  const currency = item.downloadCurrency || (tracks[0]?.downloadCurrency || 'eur');
  // Swish is available when currency is SEK (or user is Swedish and the artist accepts SEK)
  const showSwish = isSwedish && currency === 'sek';

  if (!allowDl) return null;

  const currencySymbols: Record<string, string> = {
    eur: '€',
    usd: '$',
    gbp: '£',
    cad: '$',
    sek: 'kr',
  };
  const symbol = currencySymbols[currency] || '€';
  const displayPrice = (cents: number) => (cents / 100).toFixed(2);

  // Super admin + admins + the creator who originally uploaded this track
  // all bypass the paywall and get a direct free download.
  const isUploader = !!(user?.uid && (
    user.uid === item.uploadedBy ||
    user.uid === item.createdBy ||
    user.uid === item.userId ||
    user.uid === item.owner?.userId
  ));
  // Policy: EVERY user — including super admins and owners — goes through the
  // paywall when downloading. No client-side bypass.
  const canBypassPaywall = false;

  /** Gate all download actions — redirect to sign-in if not logged in */
  const requireAuth = (): boolean => {
    if (user) return true;
    router.push(`/signin?next=${encodeURIComponent(window.location.pathname)}`);
    return false;
  };

  const handleOpenPwyw = () => {
    if (!requireAuth()) return;
    // Pre-fill name from profile if available
    if (userProfile && !buyerName) {
      const name = (userProfile as any).nickname || userProfile.displayName || '';
      setBuyerName(name);
    }
    // Reset price to minimum each time dialog opens
    setUserPrice(minPrice);
    setIsOpen(true);
  };
  openRef.current = handleOpenPwyw;

  // Whether a lossless WAV copy is available for this track
  const hasWav = !!(item.hiResUrl);

  // "free" downloads go through the Name Your Price dialog with minimum=0
  // so the artist can still earn tips. Fall through to 'pwyw' case below.

  // Open the embedded, theme-matched Payment Element (Tier 2) instead of
  // redirecting to a hosted Stripe page. The buyer pays on-page; on success we
  // navigate to the payment-verified download route.
  const handlePayCheckout = () => {
    const priceCents = typeof userPrice === 'number' ? userPrice : minPrice;
    if (priceCents < minPrice) {
      alert(`Minimum price is ${symbol}${displayPrice(minPrice)}`);
      return;
    }
    const effectiveName = isAnonymous ? '' : (buyerName.trim() || 'Someone');
    const amount = Math.max(priceCents, 100); // Stripe 1.00 floor
    setIsOpen(false);
    setThemed({
      amount,
      body: {
        type: 'download',
        workId: item.id,
        title: item.title,
        coverArtUrl: item.coverArtUrl,
        minPrice: amount,
        currency,
        buyerName: effectiveName,
        isAnonymous,
        buyerUid: user?.uid || '',
        // Route funds to the creator's Connect account when onboarded; falls
        // back to the platform account otherwise.
        sellerId: item.owner?.userId || item.uploadedBy || item.userId || item.createdBy || '',
        downloadFormat: hasWav ? 'hires' : 'mp3',
        customerEmail: user?.email ?? undefined,
      },
    });
  };

  // Super admin / uploader bypass — skip paywall entirely.
  // Auth: the server only trusts a Firebase-signed idToken (raw uid query
  // params are spoofable and rejected with 402). Mint a fresh token on click
  // so the /api/download verifyIdToken path can short-circuit the paywall.
  if (canBypassPaywall) {
    return (
      <Button
        size={iconOnly ? 'icon' : 'sm'}
        variant="outline"
        disabled={authLoading}
        title={`${tDownload('downloadNow')}${hasWav ? ' WAV' : ''}`}
        aria-label={tDownload('downloadNow')}
        className={iconOnly ? 'h-8 w-8' : undefined}
        onClick={async () => {
          if (!requireAuth()) return;
          try {
            const token = await user!.getIdToken();
            window.location.href = `/api/download?workId=${item.id}&idToken=${encodeURIComponent(token)}`;
          } catch (err) {
            console.error('Failed to mint idToken for download:', err);
            alert('Could not authorise download. Please refresh and try again.');
          }
        }}
      >
        <Download className={iconOnly ? 'h-4 w-4' : 'h-4 w-4 mr-1'} />
        {!iconOnly && <>{tDownload('downloadNow')}{hasWav ? ' WAV' : ''}</>}
        {!iconOnly && hasWav && (
          <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide bg-primary/15 text-primary rounded px-1 py-0.5">
            Lossless
          </span>
        )}
      </Button>
    );
  }

  // Name Your Price — also handles 'free' (minPrice=0 lets fans download free or tip)
  if (downloadType === 'pwyw' || downloadType === 'free') {
    return (
      <>
        <Button
          size={iconOnly ? 'icon' : 'sm'}
          variant="outline"
          disabled={authLoading}
          onClick={handleOpenPwyw}
          title={`${tDownload('nameYourPrice')}${hasWav ? ' · WAV' : ''}`}
          aria-label={tDownload('nameYourPrice')}
          className={iconOnly ? 'h-8 w-8' : undefined}
        >
          <Download className={iconOnly ? 'h-4 w-4' : 'h-4 w-4 mr-1'} />
          {!iconOnly && <>{tDownload('nameYourPrice')}{hasWav ? ' · WAV' : ''}</>}
          {!iconOnly && hasWav && (
            <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide bg-primary/15 text-primary rounded px-1 py-0.5">
              Lossless
            </span>
          )}
        </Button>

        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          {/* Width is viewport-capped so a long track title (or any
              transformed ancestor that shifts the fixed dialog) can never
              push it off-screen and crop the right side. Desktop: a tidy
              420px card. Mobile: a full-width bottom sheet that clears the
              header + player bar. Gordon: "should adapt and flex to track
              info in desktop and row break in mobile." */}
          <DialogContent className="w-[calc(100vw-1.5rem)] max-w-[420px] overflow-x-hidden sm:max-w-[420px] max-sm:fixed max-sm:inset-x-0 max-sm:top-[50px] max-sm:bottom-0 max-sm:!translate-x-0 max-sm:!translate-y-0 max-sm:!left-0 max-sm:max-w-none max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:overflow-y-auto max-sm:h-auto max-sm:pb-[calc(84px+env(safe-area-inset-bottom))]">
            <DialogHeader>
              <DialogTitle className="pr-6 break-words leading-snug">{tDownload('nameYourPrice')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 min-w-0">
              {/* Track info — flexes; title wraps instead of forcing width */}
              <div className="flex gap-3 min-w-0">
                {item.coverArtUrl && (
                  <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                    <Image
                      src={item.coverArtUrl}
                      alt={item.title}
                      width={64}
                      height={64}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="capitalize font-medium break-words line-clamp-2">{item.title}</p>
                  <p className="text-sm text-muted-foreground truncate">{item.artist || tMusic('unknownArtist')}</p>
                </div>
              </div>

              {/* Minimum price hint */}
              <p className="text-sm text-muted-foreground">
                {minPrice > 0
                  ? `Minimum: ${symbol}${displayPrice(minPrice)}`
                  : 'Enter 0 to download for free, or pay what you think it\'s worth.'}
                <span className="block text-xs mt-0.5 text-muted-foreground/70">
                  Pay what you feel it&apos;s worth — your name goes on the leaderboard!
                </span>
              </p>

              {/* Price input */}
              <div>
                <label className="text-sm font-medium">Your price ({symbol})</label>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-lg font-semibold">{symbol}</span>
                  <Input
                    type="number"
                    step="0.01"
                    min={minPrice / 100}
                    value={userPrice === '' ? '' : (userPrice / 100).toFixed(2)}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === '.') {
                        setUserPrice('');
                      } else {
                        const cents = Math.round(parseFloat(val) * 100);
                        // Allow 0 (free) if minPrice is 0, otherwise enforce minimum
                        setUserPrice(cents >= minPrice ? cents : minPrice);
                      }
                    }}
                    className="flex-1"
                    placeholder={displayPrice(minPrice)}
                  />
                </div>
              </div>

              {/* Name for leaderboard */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Your name on the leaderboard</label>
                <Input
                  value={isAnonymous ? '' : buyerName}
                  onChange={e => setBuyerName(e.target.value)}
                  placeholder="Your name or alias"
                  disabled={isAnonymous}
                  className="h-9"
                />
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={isAnonymous}
                    onChange={e => setIsAnonymous(e.target.checked)}
                    className="accent-primary"
                  />
                  Stay anonymous (your payment still counts, just no name shown)
                </label>
              </div>

              {/* Pay buttons */}
              <div className="flex gap-2 pt-2">
                {showSwish && (
                  <Button
                    className="flex-1 bg-[#00B67A] hover:bg-[#00a06d] text-white font-semibold"
                    onClick={() => {
                      const priceCents = typeof userPrice === 'number' ? userPrice : minPrice;
                      if (priceCents < minPrice) {
                        alert(`Minimum price is ${symbol}${displayPrice(minPrice)}`);
                        return;
                      }
                      setSwishAmountSEK(priceCents / 100);
                      setIsOpen(false);
                      setSwishOpen(true);
                    }}
                    disabled={typeof userPrice === 'string' && userPrice === ''}
                  >
                    Pay via Swish
                  </Button>
                )}
                <Button
                  className="flex-1"
                  onClick={handlePayCheckout}
                  disabled={typeof userPrice === 'string' && userPrice === ''}
                >
                  {showSwish ? 'Pay via Card' : `Pay ${symbol}${typeof userPrice === 'number' ? displayPrice(userPrice) : '—'} & Download`}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Swish checkout for PWYW */}
        <SwishCheckoutDialog
          open={swishOpen}
          onOpenChange={setSwishOpen}
          amountSEK={swishAmountSEK}
          message={`Download: ${item.title}`}
          itemId={item.id}
          itemType="download"
          itemTitle={item.title}
          buyerId={user?.uid}
          buyerEmail={user?.email ?? undefined}
          onSuccess={() => {
            setSwishOpen(false);
            window.location.href = `/api/download?workId=${item.id}`;
          }}
        />

        {/* Tier-2 embedded themed checkout */}
        {themed && (
          <ThemedPaymentDialog
            open={!!themed}
            onOpenChange={(o) => { if (!o) setThemed(null); }}
            body={themed.body}
            amountCents={themed.amount}
            currency={currency}
            title={item.title}
            subtitle={item.artist || tMusic('unknownArtist')}
            coverArtUrl={item.coverArtUrl}
            payLabel="Pay & download"
            onPaid={(piId) => {
              setThemed(null);
              window.location.href = `/api/download?payment_intent=${encodeURIComponent(piId)}&workId=${encodeURIComponent(item.id)}`;
            }}
          />
        )}
      </>
    );
  }

  // Fixed price / Paid — coπ's "download" only brings the button into view;
  // a voice command never starts a payment by itself.
  openRef.current = () => document.getElementById('bb-download-buy')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  return (
    <>
      <div id="bb-download-buy" className="flex gap-2">
        {showSwish && (
          <Button
            size="sm"
            className="bg-[#00B67A] hover:bg-[#00a06d] text-white font-semibold"
            disabled={authLoading}
            onClick={() => {
              if (!requireAuth()) return;
              setSwishAmountSEK(minPrice / 100);
              setSwishOpen(true);
            }}
          >
            <Download className="h-4 w-4 mr-1" />
            Buy via Swish
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          disabled={authLoading}
          onClick={() => {
            if (!requireAuth()) return;
            const amount = Math.max(minPrice, 100);
            setThemed({
              amount,
              body: {
                type: 'download',
                workId: item.id,
                title: item.title,
                coverArtUrl: item.coverArtUrl,
                minPrice: amount,
                currency,
                buyerUid: user?.uid || '',
                sellerId: item.owner?.userId || item.uploadedBy || item.userId || item.createdBy || '',
                downloadFormat: hasWav ? 'hires' : 'mp3',
                customerEmail: user?.email ?? undefined,
              },
            });
          }}
        >
          <Download className="h-4 w-4 mr-1" />
          {showSwish ? 'Buy via Card' : `Buy ${symbol}${displayPrice(minPrice)}`}
        </Button>
      </div>

      {/* Swish checkout for fixed price */}
      <SwishCheckoutDialog
        open={swishOpen}
        onOpenChange={setSwishOpen}
        amountSEK={swishAmountSEK}
        message={`Download: ${item.title}`}
        itemId={item.id}
        itemType="download"
        itemTitle={item.title}
        buyerId={user?.uid}
        buyerEmail={user?.email ?? undefined}
        onSuccess={() => {
          setSwishOpen(false);
          window.location.href = `/api/download?workId=${item.id}`;
        }}
      />

      {/* Tier-2 embedded themed checkout (fixed-price) */}
      {themed && (
        <ThemedPaymentDialog
          open={!!themed}
          onOpenChange={(o) => { if (!o) setThemed(null); }}
          body={themed.body}
          amountCents={themed.amount}
          currency={currency}
          title={item.title}
          subtitle={item.artist || tMusic('unknownArtist')}
          coverArtUrl={item.coverArtUrl}
          payLabel="Pay & download"
          onPaid={(piId) => {
            setThemed(null);
            window.location.href = `/api/download?payment_intent=${encodeURIComponent(piId)}&workId=${encodeURIComponent(item.id)}`;
          }}
        />
      )}
    </>
  );
}

// ─── ProjectFileButton ────────────────────────────────────────────────────────
// Sells the track's DAW/sampler PROJECT FILE — e.g. a Teenage Engineering
// EP-133 K.O. II / EP-1320 session — as its own pay-what-you-feel product
// (Gordon: "sell tracks as Teenage Engineering EP series project file
// download, pay what you feel"). Renders only when the work has a project
// file attached (Media Vault edit → Project file). The work's owner gets
// their own file directly; everyone else goes through the PWYW paywall.
function ProjectFileButton({ item, iconOnly = false }: { item: any; iconOnly?: boolean }) {
  const { user, userProfile, loading: authLoading } = useAuth();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [userPrice, setUserPrice] = useState<number | ''>(item.projectFileMinPrice ?? 0);
  const [buyerName, setBuyerName] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [themed, setThemed] = useState<{ amount: number; body: any } | null>(null);

  const projectFileCount = Array.isArray(item?.projectFiles) && item.projectFiles.length
    ? item.projectFiles.length
    : (item?.projectFileUrl ? 1 : 0);
  if (!projectFileCount) return null;

  const minPrice = item.projectFileMinPrice ?? 0;
  const currency = item.downloadCurrency || 'eur';
  const currencySymbols: Record<string, string> = { eur: '€', usd: '$', gbp: '£', cad: '$', sek: 'kr' };
  const symbol = currencySymbols[currency] || '€';
  const displayPrice = (cents: number) => (cents / 100).toFixed(2);
  const formatLabel = item.projectFileFormat || 'Project file';
  const isOwner = !!(user?.uid && user.uid === item.owner?.userId);

  const handleOpen = () => {
    if (!user) {
      router.push(`/signin?next=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    if (isOwner) {
      // The creator grabs their own project file without paying themselves.
      user.getIdToken().then(t => {
        window.location.href = `/api/download?workId=${encodeURIComponent(item.id)}&kind=project&idToken=${encodeURIComponent(t)}`;
      }).catch(() => alert('Could not authorise download. Please refresh and try again.'));
      return;
    }
    if (userProfile && !buyerName) {
      setBuyerName((userProfile as any).nickname || userProfile.displayName || '');
    }
    setUserPrice(minPrice);
    setIsOpen(true);
  };

  const handlePay = () => {
    const priceCents = typeof userPrice === 'number' ? userPrice : minPrice;
    if (priceCents < minPrice) {
      alert(`Minimum price is ${symbol}${displayPrice(minPrice)}`);
      return;
    }
    const amount = Math.max(priceCents, 100); // Stripe 1.00 floor
    setIsOpen(false);
    setThemed({
      amount,
      body: {
        type: 'download',
        fileKind: 'project',
        workId: item.id,
        title: `${item.title} — ${formatLabel}`,
        coverArtUrl: item.coverArtUrl,
        minPrice: amount,
        currency,
        buyerName: isAnonymous ? '' : (buyerName.trim() || 'Someone'),
        isAnonymous,
        buyerUid: user?.uid || '',
        sellerId: item.owner?.userId || item.uploadedBy || item.userId || item.createdBy || '',
        customerEmail: user?.email ?? undefined,
      },
    });
  };

  return (
    <>
      <Button
        size={iconOnly ? "icon" : "sm"}
        variant="outline"
        disabled={authLoading}
        onClick={handleOpen}
        title={`${formatLabel} — pay what you feel`}
        aria-label={`Buy project file: ${formatLabel}`}
        data-help-title="Project file download"
        data-help-description="Get the actual project/session file for this track — open it on your own device or in your DAW, remix it, learn from it. Pay what you feel."
      >
        <Package className={iconOnly ? "h-4 w-4" : "h-4 w-4 mr-1"} />
        {!iconOnly && <>
          {formatLabel}
          <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide bg-primary/15 text-primary rounded px-1 py-0.5">
            Project
          </span>
        </>}
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="pr-6 break-words leading-snug">Project file — pay what you feel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 min-w-0">
            <div className="flex gap-3 min-w-0">
              {item.coverArtUrl && (
                <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                  <Image src={item.coverArtUrl} alt={item.title} width={64} height={64} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="capitalize font-medium break-words line-clamp-2">{item.title}</p>
                <p className="text-sm text-muted-foreground truncate">{formatLabel}</p>
                <p className="text-xs text-muted-foreground/70 mt-0.5">
                  {projectFileCount > 1
                    ? `${projectFileCount} project files — the complete setup, zipped together.`
                    : 'The full project/session file — open it, tweak it, make it yours.'}
                </p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              {minPrice > 0
                ? `Minimum: ${symbol}${displayPrice(minPrice)}`
                : 'Pay what you feel it’s worth.'}
            </p>
            <div>
              <label className="text-sm font-medium">Your price ({symbol})</label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-lg font-semibold">{symbol}</span>
                <Input
                  type="number"
                  step="0.01"
                  min={minPrice / 100}
                  value={userPrice === '' ? '' : (userPrice / 100).toFixed(2)}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || val === '.') setUserPrice('');
                    else {
                      const cents = Math.round(parseFloat(val) * 100);
                      setUserPrice(cents >= minPrice ? cents : minPrice);
                    }
                  }}
                  className="flex-1"
                  placeholder={displayPrice(minPrice)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Your name (optional)</label>
              <Input
                value={isAnonymous ? '' : buyerName}
                onChange={e => setBuyerName(e.target.value)}
                placeholder="Your name or alias"
                disabled={isAnonymous}
                className="h-9"
              />
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input type="checkbox" checked={isAnonymous} onChange={e => setIsAnonymous(e.target.checked)} className="accent-primary" />
                Stay anonymous
              </label>
            </div>
            <Button className="w-full" onClick={handlePay} disabled={typeof userPrice === 'string' && userPrice === ''}>
              Pay {symbol}{typeof userPrice === 'number' ? displayPrice(userPrice) : '—'} & Download project
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {themed && (
        <ThemedPaymentDialog
          open={!!themed}
          onOpenChange={(o) => { if (!o) setThemed(null); }}
          body={themed.body}
          amountCents={themed.amount}
          currency={currency}
          title={`${item.title} — ${formatLabel}`}
          subtitle="Project file"
          coverArtUrl={item.coverArtUrl}
          payLabel="Pay & download project"
          onPaid={(piId) => {
            setThemed(null);
            window.location.href = `/api/download?payment_intent=${encodeURIComponent(piId)}&workId=${encodeURIComponent(item.id)}&kind=project`;
          }}
        />
      )}
    </>
  );
}

// ─── ReleaseDownloadButton ────────────────────────────────────────────────────
// Shows a dialog listing every track in a release so fans can download
// each one individually. Works with both linked works (workId) and
// raw audio URLs embedded directly in the release tracks array.
function ReleaseDownloadButton({ item, tracks, iconOnly = false }: { item: any; tracks: any[]; iconOnly?: boolean }) {
  const tDownload = useTranslations('download');
  const { user, loading: authLoading } = useAuth();
  const { isSuperAdmin, isAdmin } = useAdmin();
  const router = useRouter();
  const { toast } = useToast();
  const { playTrack: dlgPlayTrack } = useMediaPlayer();
  const [open, setOpen] = useState(false);
  // Bundle PWYW: null = collapsed, number = chosen price in cents. The whole
  // release ZIP ALWAYS goes through Stripe (no owner/admin bypass on the
  // front end — admins use Media Vault), so the buyer flow is always testable.
  const [zipPrice, setZipPrice] = useState<number | null>(null);
  // Tier-2 embedded themed checkout for the whole-release ZIP.
  const [themedZip, setThemedZip] = useState<{ amount: number; body: any; idsCsv: string; zipFilename: string } | null>(null);

  // Only show when there are tracks with audio to download
  const downloadableTracks = tracks.filter((t: any) => t.audioUrl || t.workId);
  if (downloadableTracks.length === 0) return null;

  // Whole-release minimum = €1 per track that goes in the ZIP (Gordon's rule),
  // honouring any higher per-release floor. Presets snap to that minimum so the
  // first chip is always the floor; lower round amounts are dropped.
  const zipTrackCount = downloadableTracks.filter((t: any) => t.workId).length;
  const zipFloor = Math.max(zipTrackCount * 100, (item.downloadMinPrice ?? 0) as number, 100);
  const zipPresets = Array.from(new Set([zipFloor, ...[100, 300, 500, 1000, 2000, 5000, 10000].filter(v => v >= zipFloor)])).sort((a, b) => a - b);
  // Streams-equivalent: how much support this amount delivers vs streaming.
  // Mirrors /why-bb (Spotify ≈ €0.0035/stream, artist net after Stripe + 6% BB).
  const zipStreams = (cents: number) => {
    const gross = cents / 100;
    const net = Math.max(0, gross - (gross * 0.015 + 0.25) - gross * 0.06);
    return Math.round(net / 0.0035);
  };

  // Super admin / admin / original uploader bypass paywall
  const isUploader = !!(user?.uid && (
    user.uid === item.uploadedBy ||
    user.uid === item.createdBy ||
    user.uid === item.userId ||
    user.uid === item.owner?.userId
  ));
  // Policy: EVERY user — including super admins and owners — goes through the
  // paywall when downloading. No client-side bypass.
  const canBypassPaywall = false;
  const releaseDownloadType = item.downloadType || 'pwyw';

  const requireAuth = (): boolean => {
    if (user) return true;
    router.push(`/signin?next=${encodeURIComponent(window.location.pathname)}`);
    return false;
  };

  // Legacy tracks have their audio embedded directly on the release (no workId
  // → no work doc to paywall, and the URL is already public). Rather than the
  // dead-end "contact the artist" alert, download the file directly. WorkId
  // tracks go through the PWYW DownloadButton in the list instead.
  const handleDownloadTrack = async (track: any) => {
    if (!requireAuth()) return;
    const url = track.audioUrl;
    if (!url) {
      alert("This track isn't available for download — please contact the artist.");
      return;
    }
    const name = `${(track.title || item.title || 'track').replace(/[^a-z0-9-_ ]/gi, '_')}`;
    try {
      // Fetch → blob forces a real download when CORS allows (Firebase Storage
      // download URLs do); otherwise fall back to opening the file.
      const res = await fetch(url);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch {
      window.open(url, '_blank', 'noopener');
    }
  };

  return (
    <>
      <Button size={iconOnly ? "icon" : "sm"} variant="outline" disabled={authLoading} onClick={() => { if (requireAuth()) setOpen(true); }}
        aria-label={tDownload('title')} title={tDownload('title')}>
        <Download className={iconOnly ? "h-4 w-4" : "h-4 w-4 mr-1"} />
        {!iconOnly && tDownload('title')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        {/* Mobile: pin as a full-height bottom sheet that clears the fixed
            header (50px) and the media-player bar + VU strip at the bottom. */}
        <DialogContent className="sm:max-w-[460px] max-sm:fixed max-sm:inset-x-0 max-sm:top-[50px] max-sm:bottom-0 max-sm:!translate-x-0 max-sm:!translate-y-0 max-sm:!left-0 max-sm:max-w-none max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:overflow-y-auto max-sm:h-auto max-sm:pb-[calc(84px+env(safe-area-inset-bottom))]">
          <DialogHeader>
            <DialogTitle>{tDownload('title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1 max-h-[60vh] max-sm:max-h-none overflow-y-auto pr-1">
            {/* Release cover + title + Download All button */}
            <div className="flex items-center gap-3 pb-3 border-b border-border/50 mb-2">
              {item.coverArtUrl && (
                <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden">
                  <Image src={item.coverArtUrl} alt={item.title} width={48} height={48} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <MarqueeText text={item.title} className="font-medium text-sm" />
                <p className="text-xs text-muted-foreground truncate">{tracks.length} track{tracks.length !== 1 ? 's' : ''}</p>
              </div>
              {/* Download All as ZIP — single archive of every track in the release.
                  Always goes through /api/download/zip (paywall enforced):
                    • Owner / super-admin: idToken bypass
                    • Stripe session covering all tracks: bundle workIds match
                    • Anything else: 402 → Stripe checkout (album bundle)

                  IMPORTANT: we ALWAYS mint the idToken when a user is signed in,
                  even if canBypassPaywall reads false client-side. The useAdmin
                  hook resolves async — clicking before it resolves used to send
                  a bare URL and the server (correctly) rejected with 402, which
                  is what Gordon hit on the MAGNONIC DREAMS ZIP. The server is
                  the source of truth for auth; the client just hands it the
                  token and lets it decide. */}
              {downloadableTracks.filter((t: any) => t.workId).length >= 2 && (
                <Button
                  size="sm"
                  variant="outline"
                  title="Download every track as a single ZIP"
                  onClick={() => setZipPrice(p => p === null ? zipFloor : null)}
                >
                  <Download className="h-3.5 w-3.5 mr-1" />Download All (ZIP)
                </Button>
              )}
            </div>

            {/* Bundle PWYW — always routes through Stripe (no owner/admin
                bypass on the front end; admins use Media Vault for direct
                downloads). Pick a price, pay, and Stripe redirects straight to
                the verified ZIP. */}
            {zipPrice !== null && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 mb-2 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary text-center">🌾 Pay What You Feel — whole release</p>
                <p className="text-center text-[11px] text-muted-foreground">
                  Minimum <span className="font-semibold text-foreground">€{(zipFloor / 100).toFixed(0)}</span> — that&apos;s just €1 per track ({zipTrackCount} tracks).
                </p>
                <div className="flex flex-wrap justify-center gap-1.5">
                  {zipPresets.map(c => (
                    <button key={c} type="button" onClick={() => setZipPrice(c)}
                      className={cn("rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors",
                        zipPrice === c ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/50")}>
                      €{(c / 100).toFixed(0)}{c === zipFloor ? " min" : ""}
                    </button>
                  ))}
                </div>
                <p className="text-center text-[11px] text-muted-foreground">…or type your own amount — pay whatever it&apos;s worth to you 🌾</p>
                <div className="flex items-center gap-2 justify-center min-w-0">
                  <span className="text-3xl font-bold shrink-0">€</span>
                  <Input type="number" step="0.01" min={zipFloor / 100}
                    value={(zipPrice / 100).toFixed(2)}
                    onChange={e => setZipPrice(Math.max(Math.round((parseFloat(e.target.value) || 0) * 100), zipFloor))}
                    // Big digits; the field grows with the amount (ch-based) but
                    // never overflows the dialog (max-w-full).
                    style={{ width: `${Math.max(((zipPrice / 100).toFixed(2)).length + 2, 6)}ch` }}
                    className="h-14 max-w-full text-center !text-3xl font-bold tabular-nums px-2" />
                </div>
                {/* What this support equals vs streaming — for the buyer */}
                {zipStreams(zipPrice) > 0 && (
                  <p className="text-center text-[11px] text-muted-foreground">
                    ≈ <span className="font-semibold text-foreground">{zipStreams(zipPrice).toLocaleString()}</span> Spotify streams of support to the artist 🌾
                  </p>
                )}
                <Button
                  className="w-full"
                  onClick={() => {
                    const ids = downloadableTracks.map((t: any) => t.workId).filter(Boolean);
                    if (ids.length < 2) { toast({ title: 'Bundle needs ≥2 tracks with work IDs', variant: 'destructive' }); return; }
                    const amount = Math.max(zipPrice || zipFloor, zipFloor);
                    const idsCsv = ids.join(',');
                    // Human-readable — strip only characters a filesystem can't
                    // store, keep real spacing/capitalization (was collapsing
                    // everything to underscores before).
                    const zipFilename = encodeURIComponent(
                      (item.title || 'release').replace(/["/\\]/g, "'").replace(/[<>:|?*\x00-\x1F]/g, '').trim()
                    );
                    // Hand off to the embedded themed Payment Element; close this
                    // dialog first to avoid stacked focus traps.
                    setOpen(false);
                    setZipPrice(null);
                    setThemedZip({
                      amount, idsCsv, zipFilename,
                      body: {
                        type: 'release-bundle',
                        workIds: ids,
                        trackCount: ids.length,
                        currency: item.downloadCurrency || 'eur',
                        title: item.title,
                        coverArtUrl: item.coverArtUrl,
                        buyerName: user?.displayName ?? '',
                        buyerUid: user?.uid || '',
                        customerEmail: user?.email ?? undefined,
                      },
                    });
                  }}
                >
                  <Download className="h-3.5 w-3.5 mr-1" />{`Pay €${(zipPrice / 100).toFixed(2)} & Download All`}
                </Button>
              </div>
            )}

            {/* Track list with individual download buttons */}
            {tracks.map((track: any, i: number) => (
              <div key={track.id || i} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-secondary/50 transition-colors group">
                <span className="w-5 text-right text-xs text-muted-foreground flex-shrink-0 group-hover:hidden touch:!hidden">{i + 1}</span>
                {/* Play button — appears on hover over the track number */}
                {(track.audioUrl || track.mp3Url) && (
                  <button
                    type="button"
                    className="w-5 h-5 hidden group-hover:flex touch:!flex items-center justify-center flex-shrink-0 text-primary"
                    title={`Play ${track.title}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      dlgPlayTrack({
                        id: track.workId || track.id,
                        title: track.title,
                        artist: track.artist?.name || track.artistName || item.artist || '',
                        imageUrl: track.coverArtUrl || item.coverArtUrl || '',
                        audioUrl: track.audioUrl || track.mp3Url || '',
                      } as any);
                    }}
                  >
                    <Play className="h-3.5 w-3.5 fill-current" />
                  </button>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <MarqueeText text={track.title} className="text-sm font-medium min-w-0 flex-1" />
                    {track.explicit && <ExplicitBadge explicit size={13} className="flex-shrink-0" />}
                    {track.version && track.version !== 'Original' && (
                      <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-primary/80 bg-primary/10 rounded px-1 py-0.5">{track.version}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{track.artist?.name || track.artistName || ''}</p>
                </div>
                {track.workId ? (
                  // Per-track PWYW: reuse DownloadButton so the "pay what you
                  // feel" popup opens (instead of jumping straight to Stripe).
                  // Force pwyw + allowDownloads so every track is downloadable
                  // by default.
                  <div className="flex-shrink-0">
                    <DownloadButton
                      iconOnly
                      item={{
                        id: track.workId,
                        title: track.title || item.title,
                        artist: track.artist?.name || track.artistName || item.artist,
                        coverArtUrl: track.coverArtUrl || item.coverArtUrl,
                        downloadType: track.downloadType || 'pwyw',
                        downloadMinPrice: track.downloadMinPrice ?? item.downloadMinPrice ?? 0,
                        downloadCurrency: track.downloadCurrency || item.downloadCurrency || 'eur',
                        hiResUrl: track.hiResUrl || track.wavUrl,
                        allowDownloads: true,
                      }}
                      tracks={[track]}
                    />
                  </div>
                ) : track.audioUrl ? (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 flex-shrink-0 opacity-60 group-hover:opacity-100"
                    title={`Download ${track.title}`}
                    onClick={() => handleDownloadTrack(track)}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                ) : (
                  <span className="w-7 h-7 flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Tier-2 embedded themed checkout for the whole-release ZIP. On success
          Stripe stays on-page; we then hit the PaymentIntent-verified ZIP route. */}
      {themedZip && (
        <ThemedPaymentDialog
          open={!!themedZip}
          onOpenChange={(o) => { if (!o) setThemedZip(null); }}
          body={themedZip.body}
          amountCents={themedZip.amount}
          currency={item.downloadCurrency || 'eur'}
          title={`${item.title} — full release`}
          subtitle={`${themedZip.body.trackCount} tracks`}
          coverArtUrl={item.coverArtUrl}
          payLabel="Pay & download all"
          onPaid={(piId) => {
            const url = `/api/download/zip?payment_intent=${encodeURIComponent(piId)}&workIds=${encodeURIComponent(themedZip.idsCsv)}&filename=${themedZip.zipFilename}`;
            setThemedZip(null);
            window.location.href = url;
          }}
        />
      )}
    </>
  );
}

// Safely normalize credits to the expected array format
function normalizeCredits(raw: any): { role: string; name: string; href?: string; avatarUrl?: string }[] {
  if (!raw) return [];
  // Handle array format
  if (Array.isArray(raw)) {
    return raw.flatMap((c: any) => {
      if (typeof c === 'string') return [{ role: 'Contributor', name: c }];
      if (typeof c !== 'object' || !c) return [];
      // One credit line per role — a contributor with roles ['Co-Producer',
      // 'Mixing Engineer'] used to show only the first (Gordon 2026-10-31).
      const roles: string[] = Array.isArray(c.roles) && c.roles.length
        ? [...new Set(c.roles.map((r: any) => String(r)).filter(Boolean))] as string[]
        : [String(c.role || c.type || c.credit || c.creditType || 'Contributor')];
      const name = String(c.name || c.displayName || c.contributor || c.artistName || c.person || 'Unknown');
      const href = c.href || c.link || c.profileUrl || (c.slug ? `/artists/${c.slug}` : undefined) || (c.artistId ? `/artists/${c.artistId}` : undefined) || (c.userId ? `/profile/${c.userId}` : undefined) || (c.docId ? `/artists/${c.docId}` : undefined);
      const avatarUrl = c.avatarUrl || c.avatar || c.imageUrl || c.thumbnailUrl || undefined;
      // Liner notes name the instrument: "Saxophone: Gunnel Samuelsson", not "Musician" (Gordon 2026-11-03).
      const instrument = String(c.instrument || '').trim().replace(/\b\w/g, ch => ch.toUpperCase());
      return roles.map(role => ({ role: instrument && /^(musician|session musician|instrumentalist|performer)$/i.test(role) ? instrument : role, name, href, avatarUrl }));
    }).filter((c: any) => c.name && c.name !== 'Unknown');
  }
  // Handle object format { executiveProducer: "Name", producer: "Name, Name2" }
  if (typeof raw === 'object') {
    const roleLabels: Record<string, string> = {
      executiveProducer: 'Executive Producer',
      executive_producer: 'Executive Producer',
      producer: 'Producer',
      primaryArtist: 'Primary Artist',
      primary_artist: 'Primary Artist',
      featuredArtist: 'Featured Artist',
      featured_artist: 'Featured Artist',
      vocalist: 'Vocalist',
      writer: 'Writer',
      songwriter: 'Songwriter',
      composer: 'Composer',
      lyricist: 'Lyricist',
      engineer: 'Engineer',
      mixedBy: 'Mixed By',
      mixed_by: 'Mixed By',
      mixer: 'Mixer',
      masteredBy: 'Mastered By',
      mastered_by: 'Mastered By',
      mastering: 'Mastering',
      recordedBy: 'Recorded By',
      recorded_by: 'Recorded By',
      musician: 'Musician',
      dj: 'DJ',
      mc: 'MC',
      rapper: 'Rapper',
      singer: 'Singer',
      arranger: 'Arranger',
      conductor: 'Conductor',
      remixer: 'Remixer',
      featuring: 'Featuring',
      artist: 'Artist',
      performer: 'Performer',
      instrumentalist: 'Instrumentalist',
      backing_vocals: 'Backing Vocals',
      backingVocals: 'Backing Vocals',
      artwork: 'Artwork',
      photography: 'Photography',
      design: 'Design',
      label: 'Label',
      publisher: 'Publisher',
    };
    const credits: { role: string; name: string; href?: string; avatarUrl?: string }[] = [];
    for (const [key, value] of Object.entries(raw)) {
      if (!value) continue;
      const role = roleLabels[key] || key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^./, s => s.toUpperCase()).trim();
      if (typeof value === 'string') {
        value.split(',').map(n => n.trim()).filter(Boolean).forEach(name => {
          credits.push({ role, name });
        });
      } else if (Array.isArray(value)) {
        value.forEach((v: any) => {
          if (typeof v === 'string') credits.push({ role, name: v });
          else if (v?.name || v?.displayName) {
            const name = v.name || v.displayName;
            const href = v.href || v.link || v.profileUrl || (v.slug ? `/artists/${v.slug}` : undefined) || (v.artistId ? `/artists/${v.artistId}` : undefined) || (v.userId ? `/profile/${v.userId}` : undefined) || (v.docId ? `/artists/${v.docId}` : undefined);
            credits.push({ role, name, href });
          }
        });
      } else if (typeof value === 'object' && (value as any).name) {
        const v = value as any;
        const href = v.href || v.link || v.profileUrl || (v.slug ? `/artists/${v.slug}` : undefined) || (v.artistId ? `/artists/${v.artistId}` : undefined) || (v.userId ? `/profile/${v.userId}` : undefined);
        credits.push({ role, name: v.name, href });
      }
    }
    return credits;
  }
  return [];
}

// Extract all possible credits from a release document
function extractAllCredits(item: any): { role: string; name: string; href?: string; avatarUrl?: string }[] {
  const all: { role: string; name: string; href?: string; avatarUrl?: string }[] = [];
  // Main credits field
  all.push(...normalizeCredits(item.credits));
  // Contributors field
  all.push(...normalizeCredits(item.contributors));
  // Personnel field
  all.push(...normalizeCredits(item.personnel));
  // People field
  all.push(...normalizeCredits(item.people));
  // Artist as primary artist
  if (item.artist?.name && !all.some(c => c.name === item.artist.name && c.role === 'Primary Artist')) {
    all.push({ role: 'Primary Artist', name: item.artist.name, href: item.artist?.docId ? `/artists/${item.artist.docId}` : undefined });
  }
  // Featured artists
  if (item.featuredArtists && Array.isArray(item.featuredArtists)) {
    item.featuredArtists.forEach((fa: any) => {
      const name = typeof fa === 'string' ? fa : fa?.name || fa?.displayName || '';
      const href = (typeof fa === 'object' && fa)
        ? (fa.href || fa.link || (fa.slug ? `/artists/${fa.slug}` : undefined) || (fa.artistId ? `/artists/${fa.artistId}` : undefined) || (fa.docId ? `/artists/${fa.docId}` : undefined) || (fa.userId ? `/profile/${fa.userId}` : undefined))
        : undefined;
      // Unmatched (string-name) feat artists still get linked by the name-lookup
      // enrichment effect below.
      if (name) all.push({ role: 'Featured Artist', name, href });
    });
  }
  // Label
  if (item.label?.name) {
    all.push({ role: 'Label', name: item.label.name });
  } else if (typeof item.label === 'string' && item.label) {
    all.push({ role: 'Label', name: item.label });
  }
  // Copyright holder
  if (item.copyrightHolder && !all.some(c => c.name === item.copyrightHolder)) {
    all.push({ role: 'Copyright', name: item.copyrightHolder });
  }
  return all;
}

// Build credits from track-level writers/producers/performers arrays
function buildTrackCredits(track: any): { role: string; name: string; href?: string }[] {
  const credits: { role: string; name: string; href?: string }[] = [];
  if (track.writers && Array.isArray(track.writers)) {
    track.writers.forEach((w: any) => {
      if (typeof w === 'string') {
        const name = w.replace(/-/g, ' ');
        if (name) credits.push({ role: 'Writer', name });
      } else if (w) {
        const name = w.entityRef?.name || w.name || w.displayName || '';
        const href = w.slug ? `/artists/${w.slug}` : w.artistId ? `/artists/${w.artistId}` : w.userId ? `/profile/${w.userId}` : undefined;
        if (name) credits.push({ role: 'Writer', name, href });
      }
    });
  }
  if (track.producers && Array.isArray(track.producers)) {
    track.producers.forEach((p: any) => {
      if (typeof p === 'string') {
        const name = p.replace(/-/g, ' ');
        if (name) credits.push({ role: 'Producer', name });
      } else if (p) {
        const name = p.entityRef?.name || p.name || p.displayName || '';
        const href = p.slug ? `/artists/${p.slug}` : p.artistId ? `/artists/${p.artistId}` : p.userId ? `/profile/${p.userId}` : undefined;
        if (name) credits.push({ role: 'Producer', name, href });
      }
    });
  }
  if (track.performers && Array.isArray(track.performers)) {
    track.performers.forEach((p: any) => {
      const role = p?.role ? p.role.charAt(0).toUpperCase() + p.role.slice(1) : 'Performer';
      if (typeof p === 'string') {
        const name = p.replace(/-/g, ' ');
        if (name) credits.push({ role, name });
      } else if (p) {
        const name = p.userId?.replace(/-/g, ' ') || p.name || p.displayName || '';
        const href = p.slug ? `/artists/${p.slug}` : p.artistId ? `/artists/${p.artistId}` : p.userId ? `/profile/${p.userId}` : undefined;
        if (name) credits.push({ role, name, href });
      }
    });
  }
  return credits;
}

export default function MusicPage({ params }: { params: Promise<{ id: string }> }) {
  const t = useTranslations('music');
  const tPlayer = useTranslations('player');
  const tLyrics = useTranslations('lyrics');
  const tCommon = useTranslations('common');
  const tDownload = useTranslations('download');
  const resolvedParams = use(params);
  const [item, setItem] = useState<any>(null);
  const tNav = useTranslations('nav');
  // The release a TRACK sits on, for the breadcrumb (Gordon 2026-10-31:
  // "home; artists: artist name, releases: release title … all links").
  const [parentRelease, setParentRelease] = useState<{ id: string; title: string } | null>(null);
  useEffect(() => {
    if (!item || item.type === 'release') { setParentRelease(null); return; }
    let alive = true;
    getCoverIndex()
      .then(ix => { if (alive) setParentRelease(ix.releaseForWork({ ...item, id: item.id || resolvedParams.id })); })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, item?.type, item?.releaseId, item?.title, resolvedParams.id]);
  const [tracks, setTracks] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // A /music/<id> that is actually a SYNC-CATALOG id (the player used to hand
  // out syncTracks ids, so such links exist in the wild — Gordon hit one
  // 2026-09-11): bounce to the sync page's deep link instead of "not found".
  const [syncChecked, setSyncChecked] = useState(false);
  const [leaderboard, setLeaderboard] = useState<{ id: string; buyerName: string; isAnonymous: boolean; amountPaid: number; currency: string }[]>([]);
  const [currentTrackIdx, setCurrentTrackIdx] = useState<number | null>(null);
  const [expandedLyricsTrackId, setExpandedLyricsTrackId] = useState<string | null>(null);
  const { playTrack: globalPlayTrack, playTracks: globalPlayTracks, isPlaying: globalIsPlaying, isActive: isPlayerBarActive, analyserL, currentTrack: globalCurrentTrack, togglePlay: globalTogglePlay } = useMediaPlayer();
  const router = useRouter();
  const { user: authUser, viewAs } = useAuth();
  const { isAdmin: isAdminMain, isSuperAdmin: isSuperAdminMain } = useAdmin();
  // The owner/creator of this work|release (matches the Firestore write rules:
  // works → owner.userId, releases → createdByUid). Used so creators — not just
  // admins — can design their own cover. Suppressed while "Preview as role" is
  // active so the owner's edit affordances also hide in preview.
  const isOwnerMain = !viewAs && !!(authUser?.uid && (
    authUser.uid === item?.owner?.userId ||
    authUser.uid === item?.createdByUid ||
    authUser.uid === item?.createdBy ||
    authUser.uid === item?.userId ||
    authUser.uid === item?.uploadedBy
  ));
  const canManageArt = isAdminMain || isSuperAdminMain;
  const [uploadingArt, setUploadingArt] = useState(false);
  const [artVaultOpen, setArtVaultOpen] = useState(false);
  // Sync-licence inquiry — every track is licensable (Gordon: "you can
  // license any track really"), reusing the /synchronization inquiry flow.
  const [licenseOpen, setLicenseOpen] = useState(false);
  const [vaultImages, setVaultImages] = useState<VaultImage[]>([]);
  const [loadingVault, setLoadingVault] = useState(false);
  // Lightbox for the cover-art gallery: `null` = closed, number = active image index.
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  // ?download=1 (coπ's "download" from another page) opens the download window.
  const [autoDownload, setAutoDownload] = useState(false);
  useEffect(() => { if (new URLSearchParams(window.location.search).get('download') === '1') setAutoDownload(true); }, []);
  // Swipe in the lightbox (Gordon 2026-10-31: "swipe don't work in release
  // images … remove the slideshow arrows" on phones): the picture follows the
  // finger, a flick of 50px+ moves to the next/previous image.
  const lbTouch = useRef<{ x: number; y: number } | null>(null);
  const [lbDrag, setLbDrag] = useState(0);
  // Draft URL input for adding a new release/track video.
  const [videoDraft, setVideoDraft] = useState("");
  const [savingVideo, setSavingVideo] = useState(false);
  // Inline genre editing (owner + admin) — chips shown to everyone.
  const [genreEditOpen, setGenreEditOpen] = useState(false);
  const [genreDraft, setGenreDraft] = useState<string[]>([]);
  const [savingGenres, setSavingGenres] = useState(false);
  const { toast } = useToast();
  const artInputRef = useRef<HTMLInputElement>(null);

  // Sync-catalog rescue: when the id isn't a release/work, see if it's a
  // syncTracks doc and redirect to /synchronization?track=<id> (which deep-
  // links straight to the track). Keeps every pre-2026-09-11 shared link and
  // QR code working.
  useEffect(() => {
    if (isLoading || item || syncChecked) return;
    let cancelled = false;
    (async () => {
      try {
        const s = await getDoc(doc(db, "syncTracks", resolvedParams.id));
        if (!cancelled && s.exists()) {
          router.replace(`/synchronization?track=${resolvedParams.id}`);
          return; // keep the spinner up while the redirect lands
        }
      } catch { /* fall through to Music not found */ }
      if (!cancelled) setSyncChecked(true);
    })();
    return () => { cancelled = true; };
  }, [isLoading, item, syncChecked, resolvedParams.id, router]);
  // "You might also like" — genre-matched releases, shown below the main content.
  const [similarReleases, setSimilarReleases] = useState<any[]>([]);
  const [similarPerRow, setSimilarPerRow] = usePerRow('bb-similar-per-row');

  // Fetch once the item (and its genres) are known. Bounded scan + client-side
  // genre-overlap scoring (see lib/similar-content) — no new Firestore index.
  useEffect(() => {
    // Never show the previous page's picks while this page's load.
    setSimilarReleases([]);
    if (!item?.id) return;
    const refGenres = normGenreList(item.genres, item.genre);
    const current = { ids: [item.id, resolvedParams.id, item.releaseId], title: item.title, artist: item.artist, artistName: item.artistName };
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, "releases"), limit(120)));
        const candidates = snap.docs
          .map((d) => {
            const rd = d.data() as any;
            return {
              ...rd, id: d.id, // the document id wins over any stored `id` field
              genres: normGenreList(rd.genres, rd.genre),
              dateField: rd.releaseDate,
            };
          })
          .filter((r: any) => !r.adminHidden && !r.hidden && (isAdminMain || isSuperAdminMain || r.incomplete !== true))
          // Not the song you're on, nor the release it's on.
          .filter((r: any) => !isSelfOrParent(r, current));
        const picked = pickSimilar(candidates, refGenres, new Set([item.id, resolvedParams.id]), 12);
        if (!cancelled) setSimilarReleases(picked);
      } catch {
        if (!cancelled) setSimilarReleases([]);
      }
    })();
    return () => { cancelled = true; };
  }, [item?.id, item?.genres, item?.genre, isAdminMain, isSuperAdminMain, resolvedParams.id]);

  // Hide the global contrast-vector overlay while the user is looking at a
  // track / release that has its own uploaded cover art — the strokes would
  // otherwise compete with the artwork. The hook is reference-counted so it
  // releases cleanly on unmount + when the cover URL flips to empty.
  const hasUploadedCover = Boolean(item?.coverArtUrl || item?.imageUrl);
  useSuppressVectorOverlay(hasUploadedCover);

  // Save artwork URL to the correct Firestore collection (release or work).
  // Sets coverArtUrl to the new URL AND moves it to the front of images[].
  // Use this when the user explicitly wants the picked image as the new cover.
  const applyArtworkUrl = useCallback(async (url: string) => {
    if (!item) return;
    const id = item.id || resolvedParams.id;
    const collection2 = item.type === 'work' ? 'works' : 'releases';
    const docRef = doc(db, collection2, id);
    const existingImages = Array.isArray(item.images) ? item.images : [];
    const updatedImages = [url, ...existingImages.filter((u: string) => u !== url)];
    await updateDoc(docRef, { coverArtUrl: url, images: updatedImages });
    setItem((prev: any) => prev ? { ...prev, coverArtUrl: url, images: updatedImages } : prev);
  }, [item, resolvedParams.id]);

  // Append multiple artwork URLs to images[] without disturbing the existing
  // cover. Only sets coverArtUrl when none exists yet (first-time upload).
  // Used by the multi-file picker and the "+ Add images" gallery tile.
  const appendArtworkUrls = useCallback(async (urls: string[]) => {
    if (!item || urls.length === 0) return;
    const id = item.id || resolvedParams.id;
    const collection2 = item.type === 'work' ? 'works' : 'releases';
    const docRef = doc(db, collection2, id);
    const existingImages: string[] = Array.isArray(item.images) ? item.images : [];
    // Dedupe — never add the same URL twice.
    const merged: string[] = [...existingImages];
    for (const u of urls) {
      if (u && !merged.includes(u)) merged.push(u);
    }
    const existingCover = item.coverArtUrl || item.imageUrl || "";
    const updates: Record<string, any> = { images: merged };
    if (!existingCover && merged[0]) updates.coverArtUrl = merged[0];
    await updateDoc(docRef, updates);
    setItem((prev: any) => prev ? { ...prev, images: merged, coverArtUrl: updates.coverArtUrl || prev.coverArtUrl } : prev);
    // A release's tracks without artwork of their own show its cover (Gordon 2026-11-03).
    if (collection2 === 'releases') void propagateReleaseCover({ ...item, images: merged, coverArtUrl: updates.coverArtUrl || existingCover });
  }, [item, resolvedParams.id]);

  /** Remove one URL from images[]. If it was the cover, promote the next
   *  image (or the legacy coverArtUrl) so the release isn't left cover-less.
   *  Per Gordon: "can't remove or drag move change order in thumbnails." */
  const removeArtworkUrl = useCallback(async (url: string) => {
    if (!item) return;
    const id = item.id || resolvedParams.id;
    const collection2 = item.type === 'work' ? 'works' : 'releases';
    const docRef = doc(db, collection2, id);
    const existing: string[] = Array.isArray(item.images) ? item.images : [];
    const next = existing.filter(u => u !== url);
    const updates: Record<string, any> = { images: next };
    // The strip also shows media[] pictures — they go too.
    if (Array.isArray(item.media) && item.media.some((m: any) => (typeof m === 'string' ? m : m?.url) === url)) {
      updates.media = item.media.filter((m: any) => (typeof m === 'string' ? m : m?.url) !== url);
    }
    // If the cover was the removed image, promote the new first entry
    // (or clear cover if none remain).
    if (item.coverArtUrl === url || item.imageUrl === url) {
      updates.coverArtUrl = next[0] || '';
      if (item.imageUrl === url) updates.imageUrl = next[0] || '';
    }
    try {
      await updateDoc(docRef, updates);
    } catch (err) {
      console.error('[artwork-remove]', err);
      toast({ variant: 'destructive', title: t('artworkRemoveFailed') });
      return;
    }
    setItem((prev: any) => prev ? { ...prev, ...updates } : prev);
    if (collection2 === 'releases' && updates.coverArtUrl) void propagateReleaseCover({ ...item, images: next, coverArtUrl: updates.coverArtUrl }, { previousCovers: [url] });
  }, [item, resolvedParams.id, toast, t]);

  /** Save the strip's new order (drag to reorder). The list is the strip as
   *  shown — cover first, then images[] — so the order is written whole: the
   *  old index-based version moved the wrong picture when the cover wasn't
   *  in images[]. The first entry becomes the cover. */
  const setArtworkOrder = useCallback(async (urls: string[]) => {
    if (!item || urls.length === 0) return;
    const id = item.id || resolvedParams.id;
    const collection2 = item.type === 'work' ? 'works' : 'releases';
    const before = item.coverArtUrl || item.imageUrl || '';
    await updateDoc(doc(db, collection2, id), { images: urls, coverArtUrl: urls[0] });
    setItem((prev: any) => prev ? { ...prev, images: urls, coverArtUrl: urls[0] } : prev);
    // A new first picture is the new cover — tracks showing the old one follow.
    if (collection2 === 'releases' && urls[0] !== before) void propagateReleaseCover({ ...item, images: urls, coverArtUrl: urls[0] }, { previousCovers: before ? [before] : [] });
  }, [item, resolvedParams.id]);

  // ── Release / track videos ────────────────────────────────────────────────
  // Same dual-collection trick as the artwork helpers: works for both /music
  // (release docs in `releases`) and /music (track docs in `works`).

  const addReleaseVideo = useCallback(async () => {
    const raw = videoDraft.trim();
    if (!raw || !item) return;
    const id = item.id || resolvedParams.id;
    const collection2 = item.type === 'work' ? 'works' : 'releases';
    const docRef = doc(db, collection2, id);
    const existing: string[] = Array.isArray(item.videos) ? item.videos : [];
    if (existing.includes(raw) || item.videoUrl === raw) {
      setVideoDraft("");
      return;
    }
    setSavingVideo(true);
    try {
      // First video added → becomes the primary. Subsequent ones go into videos[].
      if (!item.videoUrl) {
        await updateDoc(docRef, { videoUrl: raw });
        setItem((prev: any) => prev ? { ...prev, videoUrl: raw } : prev);
      } else {
        const next = [...existing, raw];
        await updateDoc(docRef, { videos: next });
        setItem((prev: any) => prev ? { ...prev, videos: next } : prev);
      }
      setVideoDraft("");
    } catch (err) {
      console.error('[release-video add]', err);
    } finally {
      setSavingVideo(false);
    }
  }, [videoDraft, item, resolvedParams.id]);

  const removeReleaseVideo = useCallback(async (url: string) => {
    if (!item) return;
    const id = item.id || resolvedParams.id;
    const collection2 = item.type === 'work' ? 'works' : 'releases';
    const docRef = doc(db, collection2, id);
    setSavingVideo(true);
    try {
      if (item.videoUrl === url) {
        // Removing the primary — promote the first additional to primary, if any.
        const rest: string[] = Array.isArray(item.videos) ? [...item.videos] : [];
        const nextPrimary = rest.shift() || "";
        await updateDoc(docRef, { videoUrl: nextPrimary, videos: rest });
        setItem((prev: any) => prev ? { ...prev, videoUrl: nextPrimary, videos: rest } : prev);
      } else {
        const next: string[] = (Array.isArray(item.videos) ? item.videos : []).filter((u: string) => u !== url);
        await updateDoc(docRef, { videos: next });
        setItem((prev: any) => prev ? { ...prev, videos: next } : prev);
      }
    } catch (err) {
      console.error('[release-video remove]', err);
    } finally {
      setSavingVideo(false);
    }
  }, [item, resolvedParams.id]);

  const promoteReleaseVideoToPrimary = useCallback(async (url: string) => {
    if (!item || item.videoUrl === url) return;
    const id = item.id || resolvedParams.id;
    const collection2 = item.type === 'work' ? 'works' : 'releases';
    const docRef = doc(db, collection2, id);
    const oldPrimary = item.videoUrl || "";
    const others: string[] = (Array.isArray(item.videos) ? item.videos : []).filter((u: string) => u !== url);
    if (oldPrimary) others.unshift(oldPrimary);
    setSavingVideo(true);
    try {
      await updateDoc(docRef, { videoUrl: url, videos: others });
      setItem((prev: any) => prev ? { ...prev, videoUrl: url, videos: others } : prev);
    } catch (err) {
      console.error('[release-video promote]', err);
    } finally {
      setSavingVideo(false);
    }
  }, [item, resolvedParams.id]);

  // Upload one or more files (multi-select). Each file uploads in parallel
  // and is appended to images[]. The first uploaded image becomes the cover
  // ONLY if no cover already exists.
  const handleArtworkUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !item || !authUser) return;
    e.target.value = '';
    setUploadingArt(true);
    const folder = item.type === 'work' ? 'works' : 'releases';
    const baseId = item.id || resolvedParams.id;
    try {
      const urls: string[] = await Promise.all(files.map(async (file, i) => {
        const ext = file.name.split('.').pop() || 'jpg';
        // Add the index + a tiny random suffix so two files picked at the
        // same millisecond don't collide on path.
        const path = `covers/${folder}/${baseId}/${Date.now()}-${i}-${Math.random().toString(36).slice(2, 6)}.${ext}`;
        const snap = await uploadBytes(storageRef(storage, path), file);
        return getDownloadURL(snap.ref);
      }));
      await appendArtworkUrls(urls);
    } catch (err) {
      console.error('[artwork-upload]', err);
    } finally {
      setUploadingArt(false);
    }
  }, [item, authUser, resolvedParams.id, appendArtworkUrls]);

  // Load existing images from the vault (all releases + works that have cover art)
  const openArtVault = useCallback(async () => {
    setArtVaultOpen(true);
    if (vaultImages.length > 0) return; // already loaded
    setLoadingVault(true);
    try {
      const [relSnap, worksSnap] = await Promise.all([
        getDocs(query(collection(db, 'releases'), limit(300))),
        getDocs(query(collection(db, 'works'), limit(500))),
      ]);
      const imgs: { url: string; title: string; artist: string }[] = [];
      const seen = new Set<string>();
      const artistOf = (data: any) => String(data?.artistName || (typeof data?.artist === 'string' ? data.artist : data?.artist?.name) || '');
      const add = (url: string, title: string, artist: string) => {
        if (url && !seen.has(url)) { seen.add(url); imgs.push({ url, title, artist }); }
      };
      relSnap.docs.forEach(d => {
        const data = d.data();
        if (data.coverArtUrl) add(data.coverArtUrl, data.title || '', artistOf(data));
        if (Array.isArray(data.images)) data.images.forEach((u: string) => add(u, data.title || '', artistOf(data)));
      });
      worksSnap.docs.forEach(d => {
        const data = d.data();
        if (data.coverArtUrl) add(data.coverArtUrl, data.title || '', artistOf(data));
        if (data.imageUrl) add(data.imageUrl, data.title || '', artistOf(data));
      });
      // The current cover, then this artist's pictures first — almost always
      // what you're after. Many releases carry no artist field, so the first
      // word of the artist/title ("LeafNuts(Tougher…" → leafnuts) counts too.
      const mine = artistOf(item).toLowerCase();
      const word = (mine || String(item?.title || '').toLowerCase()).match(/[\p{L}\p{N}]{3,}/u)?.[0] || '';
      const current = item?.coverArtUrl || item?.imageUrl || '';
      const rank = (img: { url: string; title: string; artist: string }) =>
        img.url === current ? 3
        : mine && img.artist.toLowerCase() === mine ? 2
        : word && `${img.artist} ${img.title}`.toLowerCase().includes(word) ? 1 : 0;
      imgs.sort((a, b) => rank(b) - rank(a));
      setVaultImages(imgs);
    } catch (err) {
      console.error('[vault-load]', err);
    } finally {
      setLoadingVault(false);
    }
  }, [vaultImages.length, item]);

  useEffect(() => {
    async function loadMusic() {
      try {
        // Try as release first
        let docSnap = await getDoc(doc(db, "releases", resolvedParams.id));
        if (docSnap.exists()) {
          // IMPORTANT: spread data FIRST so our id/type always win over any field in the doc
          const data = { ...docSnap.data(), id: docSnap.id, type: "release" } as any;
          setItem(data);

          // Enrich artist link for release: look up by name if no slug/docId present
          if (!data.artistId && !data.artist?.docId && !data.artist?.slug) {
            const artistNameForLookup = data.artist?.name || data.artistName;
            if (artistNameForLookup) {
              try {
                const artistSnap = await getDocs(query(
                  collection(db, "artists"),
                  where("name", "==", artistNameForLookup),
                  limit(1)
                ));
                if (!artistSnap.empty) {
                  const aDoc = artistSnap.docs[0];
                  setItem((prev: any) => prev ? {
                    ...prev,
                    artistId: aDoc.id,
                    artist: { ...(prev.artist || {}), docId: aDoc.id, slug: aDoc.data().slug || aDoc.id, name: artistNameForLookup },
                  } : prev);
                }
              } catch { /* non-critical */ }
            }
          }

          // Track page view for analytics
          trackMediaEvent({
            type: 'view',
            entityType: 'release',
            entityId: docSnap.id,
            entityTitle: data.title,
            entityArtist: data.artistName || data.artist?.name,
            entityGenre: Array.isArray(data.genres) ? data.genres[0] : data.genre,
          });

          // Use the release's embedded tracks array (which has mp3Url)
          if (data.tracks && Array.isArray(data.tracks) && data.tracks.length > 0) {
            // Sort by trackNumber so the visual order always matches track 1→2→3…
            // Firestore stores arrays in insertion order which may differ.
            const sortedRaw = [...data.tracks].sort((a, b) => (a.trackNumber || 999) - (b.trackNumber || 999));
            // Release cover art — used as fallback when a track has no own thumbnail
            const releaseCover =
              data.coverArtUrl ||
              (Array.isArray(data.images) && data.images[0]) ||
              data.imageUrl ||
              "";

            const mapped = sortedRaw.map((t: any, idx: number) => ({
              // Prefer the real work id; else address by 1-based trackNumber to match
              // the per-track docs ("-track-10" = "10 Stocktown"). Using the 0-based
              // array index here linked "10 Stocktown" to "-track-9" = "09 All About
              // You" (Gordon: "click the title and I come to another track, one off").
              id: t.workId || `${docSnap.id}-track-${t.trackNumber || idx + 1}`,
              workId: t.workId || "",
              title: t.title || `Track ${t.trackNumber || idx + 1}`,
              version: t.version || "",
              trackNumber: t.trackNumber || idx + 1,
              artistName: t.artistName || data.artistName || data.artist?.name || "Unknown",
              artist: { name: t.artistName || data.artistName || data.artist?.name || "Unknown" },
              durationMs: t.durationMs || 0,
              duration: t.duration || "",
              // Prefer mp3Url (M4A/compressed streaming) over raw audioUrl (may be AIF/unsupported)
              audioUrl: t.mp3Url || t.audioUrl || t.url || "",
              // Inherit release cover when track has no own thumbnail
              // Release cover takes priority over scraped imageUrl
              coverArtUrl: t.coverArtUrl || releaseCover || t.imageUrl,
              imageUrl: t.coverArtUrl || releaseCover || t.imageUrl,
              author: t.author || "",
              lyrics: t.lyrics || "",
              credits: normalizeCredits(t.credits || t.contributors).length > 0
                ? normalizeCredits(t.credits || t.contributors)
                : buildTrackCredits(t),
            }));

            // ── Deduplicate by workId then normalised title ─────────────────
            // Release tracks[] may contain both WAV and M4A entries for the same
            // song.  Deduplicate by workId first (exact match), then by title.
            const normTitle = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
            const workIdSeen = new Set<string>();
            const titleSeen = new Set<string>();
            const deduped = mapped.filter((t: any) => {
              // Deduplicate by workId first
              if (t.workId) {
                if (workIdSeen.has(t.workId)) return false;
                workIdSeen.add(t.workId);
              }
              // Then deduplicate by normalised title
              const key = normTitle(t.title || '');
              if (!key) return true;
              if (titleSeen.has(key)) return false;
              titleSeen.add(key);
              return true;
            });
            setTracks(deduped);

            // ── Enrich tracks from work docs ────────────────────────────────
            // Pull mp3Url (streaming), credits, lyrics, duration from work docs.
            // Phase A: workId-based (direct doc lookup)
            const toEnrichA = deduped.filter((t: any) => t.workId);
            // workId → enrichment data
            const workDataMap = new Map<string, { url: string; credits: any[]; lyrics: string; durationMs: number }>();
            if (toEnrichA.length > 0) {
              try {
                const workDocs = await Promise.all(
                  toEnrichA.map((t: any) => getDoc(doc(db, "works", t.workId)))
                );
                workDocs.forEach(d => {
                  if (d.exists()) {
                    const wd = d.data();
                    const url = workAudioUrl(wd);
                    const credits = wd.contributors || wd.credits || [];
                    const lyrics = wd.lyrics || "";
                    const durationMs = wd.durationMs || (wd.durationSeconds ? wd.durationSeconds * 1000 : 0);
                    workDataMap.set(d.id, { url, credits, lyrics, durationMs });
                  }
                });
              } catch { /* non-fatal */ }
            }

            // Phase B: title-based fallback — query all works for this release
            const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, ' ').replace(/\s+/g, ' ').trim();
            const titleAudioMap = new Map<string, string>(); // normTitle → best url
            const titleCreditsMap = new Map<string, any[]>();
            try {
              const [byRelease, byArtist] = await Promise.all([
                getDocs(query(collection(db, "works"), where("releaseId", "==", docSnap.id), limit(50))),
                data.artist?.docId
                  ? getDocs(query(collection(db, "works"), where("artistId", "==", data.artist.docId), limit(100)))
                  : Promise.resolve(null),
              ]);
              [...byRelease.docs, ...(byArtist?.docs ?? [])].forEach(d => {
                const wd = d.data();
                const url = wd.mp3Url || wd.audioUrl || wd.url || "";
                const title = norm(wd.title || '');
                if (url && title && !titleAudioMap.has(title)) titleAudioMap.set(title, url);
                const credits = wd.contributors || wd.credits || [];
                if (credits.length > 0 && title && !titleCreditsMap.has(title)) titleCreditsMap.set(title, credits);
              });
            } catch { /* non-fatal */ }

            // Apply enrichment: the work doc is the recording, so its FILE wins
            // (`trackAudio`) — the release list's copy had drifted one step on
            // Extreme Paranoia (Gordon 2026-11-03: "track 2 is playing track 3").
            // Two list entries can't share a workId any more (deduped above).
            // Other fields: the release track's own data wins, the work fills gaps.
            if (workDataMap.size > 0 || titleAudioMap.size > 0) {
              setTracks((prev: any[]) =>
                prev.map((t: any) => {
                  const enriched = { ...t };
                  // workId match — fill missing data from work doc, but respect
                  // the release track's own audioUrl when it is already set.
                  if (t.workId && workDataMap.has(t.workId)) {
                    const wd = workDataMap.get(t.workId)!;
                    enriched.audioUrl = trackAudio(enriched.audioUrl, wd.url);
                    if (wd.credits.length > 0) enriched.credits = normalizeCredits(wd.credits);
                    if (wd.lyrics && !enriched.lyrics) enriched.lyrics = wd.lyrics;
                    if (wd.durationMs && !enriched.durationMs) enriched.durationMs = wd.durationMs;
                    return enriched;
                  }
                  // title match fallback
                  const normT = norm(t.title || '');
                  const titleUrl = titleAudioMap.get(normT);
                  if (titleUrl && !t.audioUrl) enriched.audioUrl = titleUrl;
                  const titleCredits = titleCreditsMap.get(normT);
                  if (titleCredits && titleCredits.length > 0 && (!enriched.credits || enriched.credits.length === 0)) {
                    enriched.credits = normalizeCredits(titleCredits);
                  }
                  return enriched;
                })
              );
            }
          } else {
            // Fallback: try works collection
            try {
              const worksQuery = query(
                collection(db, "works"),
                where("releaseId", "==", docSnap.id),
                limit(30)
              );
              const worksSnap = await getDocs(worksQuery);
              if (!worksSnap.empty) {
                setTracks(worksSnap.docs.map(d => {
                  const wd = d.data();
                  return {
                    id: d.id,
                    ...wd,
                    audioUrl: wd.audioUrl || wd.url || "",
                  };
                }));
              }
            } catch { /* works may not be indexed by releaseId */ }
          }
        } else {
          // Try as work/track
          docSnap = await getDoc(doc(db, "works", resolvedParams.id));
          // A merged duplicate lives on as a pointer: old links open the track it became.
          if (docSnap.exists() && typeof docSnap.data()?.mergedInto === "string" && docSnap.data()?.mergedInto) {
            router.replace(`/music/${docSnap.data()!.mergedInto}`);
            return;
          }
          if (docSnap.exists()) {
            const wd = docSnap.data();
            // Prefer mp3Url (compressed streaming) over raw audioUrl (may be AIF/WAV/unsupported)
            const effectiveAudioUrl = wd.mp3Url || wd.audioUrl || wd.url || "";
            const workItem = {
              id: docSnap.id,
              ...wd,
              // The discriminator MUST come after the doc spread — works carry
              // their own `type` field ("MP3", "Single", "Beat"…) which used to
              // overwrite it, so every doc write (artwork add/remove, videos,
              // cover) targeted releases/{id} and silently failed with
              // "No document to update" (Gordon: added a webp, nothing happened).
              type: "work",
              audioUrl: effectiveAudioUrl,
            };
            setItem(workItem);
            // Track page view for analytics
            trackMediaEvent({
              type: 'view',
              entityType: 'track',
              entityId: docSnap.id,
              entityTitle: wd.title,
              entityArtist: wd.artist?.name || wd.artistName,
              entityGenre: Array.isArray(wd.genres) ? wd.genres[0] : wd.genre,
            });
            // Also populate tracks so handlePlayAll can build the queue
            setTracks([workItem]);

            // ── Enrich artist link + cover art ──
            // Runs sequentially in one non-blocking task so the resolved artistId
            // is always available for the cover art layers (fixes the race condition
            // where the IIFE checked wd.artistId before name-lookup finished).
            (async () => {
              // Step 1: resolve artistId — use doc fields first, fall back to name lookup
              let resolvedArtistId = wd.artistId || wd.artist?.docId || "";
              if (!resolvedArtistId) {
                const artistNameForLookup = wd.artist?.name || wd.artistName;
                if (artistNameForLookup) {
                  try {
                    const artistSnap = await getDocs(query(
                      collection(db, "artists"),
                      where("name", "==", artistNameForLookup),
                      limit(1)
                    ));
                    if (!artistSnap.empty) {
                      const aDoc = artistSnap.docs[0];
                      resolvedArtistId = aDoc.id;
                      setItem((prev: any) => prev ? {
                        ...prev,
                        artistId: aDoc.id,
                        artist: { ...(prev.artist || {}), docId: aDoc.id, slug: aDoc.data().slug || aDoc.id, name: artistNameForLookup },
                      } : prev);
                      setTracks((prev: any[]) => prev.map(t => ({
                        ...t,
                        artistId: aDoc.id,
                      })));
                    }
                  } catch { /* non-critical */ }
                }
              }

              // Step 2: resolve cover art if the work has none
              if (!wd.coverArtUrl && !wd.imageUrl) {
                let cover = "";

                // Layer 1: Direct releaseId lookup — fastest path
                if (wd.releaseId) {
                  try {
                    const relSnap = await getDoc(doc(db, "releases", wd.releaseId));
                    if (relSnap.exists()) {
                      const r = relSnap.data();
                      cover =
                        r.coverArtUrl ||
                        (Array.isArray(r.images) && r.images[0]) ||
                        r.imageUrl ||
                        "";
                    }
                  } catch { /* non-critical */ }
                }

                // Layer 2: Search artist's releases by resolvedArtistId (now always set after name lookup)
                if (!cover && resolvedArtistId) {
                  try {
                    const relSnap = await getDocs(
                      query(collection(db, "releases"), where("artistId", "==", resolvedArtistId), limit(30))
                    );
                    const normTitle = (wd.title || "").toLowerCase().trim();
                    let anyReleaseCover = "";
                    for (const rd of relSnap.docs) {
                      const r = rd.data();
                      const relCover =
                        r.coverArtUrl ||
                        (Array.isArray(r.images) && r.images[0]) ||
                        r.imageUrl ||
                        "";
                      if (!relCover) continue;
                      if (!anyReleaseCover) anyReleaseCover = relCover; // keep first cover as fallback
                      const trackArr: any[] = Array.isArray(r.tracks) ? r.tracks : [];
                      const match =
                        trackArr.some((t: any) => (t.workId || t.id) === docSnap.id) ||
                        trackArr.some(
                          (t: any) => (t.title || "").toLowerCase().trim() === normTitle
                        );
                      if (match) { cover = relCover; break; }
                    }
                    // No track match found — still use any release cover for this artist
                    if (!cover && anyReleaseCover) cover = anyReleaseCover;
                  } catch { /* non-critical */ }
                }

                // Layer 3: Artist's own imageUrl / thumbnailUrl as last resort
                if (!cover && resolvedArtistId) {
                  try {
                    const artSnap = await getDoc(doc(db, "artists", resolvedArtistId));
                    if (artSnap.exists()) {
                      const a = artSnap.data();
                      cover =
                        a.imageUrl ||
                        a.thumbnailUrl ||
                        (Array.isArray(a.images) && a.images[0]) ||
                        "";
                    }
                  } catch { /* non-critical */ }
                }

                if (cover) {
                  // Persist the borrowed cover onto the work doc so EVERY list
                  // (vault, search, home, profile) shows it from now on —
                  // Gordon: "if a track doesn't have artwork, it should borrow
                  // the artwork from the artist profile thumbnail". Fails
                  // silently for viewers without write permission.
                  updateDoc(doc(db, "works", docSnap.id), { coverArtUrl: cover }).catch(() => {});
                  setItem((prev: any) =>
                    prev ? { ...prev, coverArtUrl: cover, imageUrl: cover } : prev
                  );
                  setTracks((prev: any[]) =>
                    prev.map((t: any) => ({
                      ...t,
                      coverArtUrl: t.coverArtUrl || cover,
                      imageUrl: t.imageUrl || cover,
                    }))
                  );
                }
              }
            })().catch(() => { /* non-critical */ });
          }
        }
      } catch (err) {
        console.error("Error loading music:", err);
      } finally {
        setIsLoading(false);
      }
    }
    loadMusic();
  }, [resolvedParams.id]);

  // Load leaderboard for this track/release
  useEffect(() => {
    if (!resolvedParams.id) return;
    const loadLeaderboard = async () => {
      try {
        const q = query(
          collection(db, 'works', resolvedParams.id, 'downloadLeaderboard'),
          orderBy('amountPaid', 'desc'),
          limit(10)
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          setLeaderboard(snap.docs.map(d => ({ id: d.id, ...d.data() } as any)));
        } else {
          // Also try as a release — leaderboard may be on the work docs linked to this release
          setLeaderboard([]);
        }
      } catch {
        // leaderboard is non-critical — silently ignore
      }
    };
    loadLeaderboard();
  }, [resolvedParams.id]);

  const coverUrl2 = item?.coverArtUrl || item?.imageUrl || "";
  const artistName2 = item?.artist?.name || item?.artistName || t('unknownArtist');

  const handlePlayTrack = (idx: number, track: any) => {
    try {
      if (!track.audioUrl) return;
      // Already the player's track (started here, on Home or anywhere):
      // pause / resume instead of restarting the album.
      if (globalCurrentTrack?.id && globalCurrentTrack.id === (track.workId || track.id)) {
        setCurrentTrackIdx(idx);
        globalTogglePlay();
        return;
      }
      setCurrentTrackIdx(idx);
      // Build full album queue so forward/backward/auto-advance works
      const allPlayable = tracks
        .filter((t: any) => t.audioUrl)
        .map((t: any, i: number) => {
          const url = String(t.audioUrl);
          const fileFormat = (() => {
            const ext = url.split('?')[0].split('.').pop()?.toUpperCase() || '';
            if (['M4A', 'MP3', 'WAV', 'FLAC', 'OGG', 'AAC', 'AIF', 'AIFF'].includes(ext)) return ext;
            if (url.includes('.m4a') || url.includes('mp4')) return 'M4A';
            return '';
          })();
          return {
            id: t.workId || t.id || `track-${i}`,
            workId: t.workId || '',
            title: String(t.title || `Track ${i + 1}`),
            artist: String(t.artist?.name || t.artistName || artistName2),
            album: String(item?.title || ""),
            duration: t.durationMs ? Math.round(t.durationMs / 1000) : 0,
            imageUrl: coverUrl2,
            audioUrl: url,
            mediaType: "audio" as const,
            genre: String(item?.genre || item?.genres?.[0] || ""),
            artistSlug: String(item?.artist?.slug || item?.artist?.docId || item?.artistId || ""),
            lyrics: String(t.lyrics || item?.lyrics || ""),
            credits: normalizeCredits(t.credits || t.contributors || item?.credits || item?.contributors),
            fileFormat: fileFormat || undefined,
            featuredArtists: Array.isArray(t.featuredArtists) ? t.featuredArtists
              : Array.isArray(item?.featuredArtists) ? item.featuredArtists
              : [],
          };
        });
      // Find the index in playable list (may differ if some tracks have no audioUrl)
      const playableIdx = allPlayable.findIndex(t => t.id === (track.workId || track.id || `track-${idx}`));
      globalPlayTracks(allPlayable, playableIdx >= 0 ? playableIdx : 0);
    } catch (err) {
      console.error("Error playing track:", err);
    }
  };

  const handlePlayAll = () => {
    try {
      // For single works, fall back to item itself if tracks wasn't populated
      const trackList = (tracks.length > 0 ? tracks : (item?.type === "work" ? [item] : []));
      const playable = trackList
        .filter((t: any) => t.audioUrl)
        .map((t: any, idx: number) => {
          const url = String(t.audioUrl);
          const fileFormat = (() => {
            const ext = url.split('?')[0].split('.').pop()?.toUpperCase() || '';
            if (['M4A', 'MP3', 'WAV', 'FLAC', 'OGG', 'AAC', 'AIF', 'AIFF'].includes(ext)) return ext;
            if (url.includes('.m4a') || url.includes('mp4')) return 'M4A';
            return '';
          })();
          return {
            id: t.workId || t.id || `track-${idx}`,
            workId: t.workId || '',
            title: String(t.title || `Track ${idx + 1}`),
            artist: String(t.artist?.name || t.artistName || artistName2),
            album: String(item?.title || ""),
            duration: t.durationMs ? Math.round(t.durationMs / 1000) : 0,
            imageUrl: coverUrl2,
            audioUrl: url,
            mediaType: "audio" as const,
            genre: String(item?.genre || item?.genres?.[0] || ""),
            artistSlug: String(item?.artist?.slug || item?.artist?.docId || item?.artistId || ""),
            lyrics: String(t.lyrics || item?.lyrics || ""),
            credits: normalizeCredits(t.credits || t.contributors || item?.credits || item?.contributors),
            fileFormat: fileFormat || undefined,
            featuredArtists: Array.isArray(t.featuredArtists) ? t.featuredArtists
              : Array.isArray(item?.featuredArtists) ? item.featuredArtists
              : [],
          };
        });
      if (playable.length > 0) {
        globalPlayTracks(playable);
        setCurrentTrackIdx(0);
      }
    } catch (err) {
      console.error("Error playing all tracks:", err);
    }
  };

  const formatDuration = (ms: number) => {
    if (!ms) return "--:--";
    const min = Math.floor(ms / 60000);
    const sec = Math.floor((ms % 60000) / 1000);
    return `${min}:${sec.toString().padStart(2, "0")}`;
  };

  const locale = useLocale();
  // Firestore Timestamp, {seconds}, epoch s/ms or a date string — '' when it
  // isn't a real date (it printed "Invalid Date").
  const formatDate = (ts: any) => {
    if (!ts) return "";
    let d: Date;
    if (typeof ts?.toDate === "function") d = ts.toDate();
    else if (typeof ts?.seconds === "number") d = new Date(ts.seconds * 1000);
    else if (typeof ts === "number") d = new Date(ts < 1e12 ? ts * 1000 : ts);
    else d = new Date(ts);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });
  };
  // The release date, else the day it was uploaded — until someone sets one
  // (Gordon 2026-11-04: "invalid date??? we need to add the date the songs are
  // uploaded until someone changes it").
  const shownDate = formatDate(item?.releaseDate) || formatDate(item?.createdAt || item?.uploadedAt || item?.dateCreated || item?.updatedAt);

  if (isLoading || (!item && !syncChecked)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Music2 className="h-12 w-12 text-muted-foreground animate-pulse" />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Disc className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Music not found</h2>
          <Link href="/feed"><Button variant="outline"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button></Link>
        </div>
      </div>
    );
  }

  const isRelease = item.type === "release";
  // Fall back to the first track's cover so the page shows the SAME artwork the
  // media player resolves (Gordon: player shows the right thumb, page doesn't).
  // A release doc often has no release-level coverArtUrl while its tracks do.
  const coverUrl = item.coverArtUrl || item.imageUrl
    || tracks.find((t: any) => t.coverArtUrl || t.imageUrl)?.coverArtUrl
    || tracks.find((t: any) => t.coverArtUrl || t.imageUrl)?.imageUrl
    || "";

  // Visitor gate — an audio-less "release" (no track has audio and it has no
  // own audio) is not something a visitor should be able to open. Owners and
  // admins still see it so they can finish/attach audio. (Gordon: "these have
  // no audio attached, why can I click on it and see as visitor?")
  const hasAnyAudio = !!(item.audioUrl || item.mp3Url)
    || tracks.some((t: any) => t.audioUrl || t.mp3Url);
  if (!hasAnyAudio && !isOwnerMain && !isAdminMain && !isSuperAdminMain) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <Disc className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Not available yet</h2>
          <p className="text-sm text-muted-foreground mb-4">This release doesn&apos;t have any audio to play yet. Check back soon.</p>
          <Link href="/home"><Button variant="outline"><ArrowLeft className="h-4 w-4 mr-2" />Back to home</Button></Link>
        </div>
      </div>
    );
  }
  // Build the full cover-art image array for the hero slideshow + gallery.
  // Releases uploaded via MultiArtworkUploader persist every artwork file in
  // item.images[] (front cover, back cover, liner notes, etc.); item.coverArtUrl
  // is the first/primary. Older releases only have coverArtUrl. We dedupe so the
  // primary doesn't appear twice when it's also in images[].
  const coverImages: string[] = (() => {
    const seen = new Set<string>();
    const out: string[] = [];
    const push = (u: any) => {
      if (typeof u === "string" && u && !seen.has(u)) { seen.add(u); out.push(u); }
    };
    push(coverUrl);
    if (Array.isArray(item.images)) item.images.forEach(push);
    if (Array.isArray(item.media)) {
      item.media.forEach((m: any) => push(typeof m === "string" ? m : m?.url));
    }
    return out;
  })();
  // `artist` is written by older paths as a plain string (e.g. bulk-upload's
  // createWork), but newer paths use an EntityRef-like object with .name.
  // Read in this order so neither shape produces "Unknown Artist" by accident.
  const artistName = (typeof item.artist === 'string' ? item.artist : item.artist?.name)
    || item.artistName
    || t('unknownArtist');

  // Precompute credits for the Credits tab
  const releaseCredits = extractAllCredits(item);
  const allTrackCredits: { role: string; name: string; href?: string; avatarUrl?: string }[] = tracks.flatMap((t: any) => buildTrackCredits(t));
  const combinedCredits = [...releaseCredits, ...allTrackCredits];
  const groupedCredits: Record<string, { name: string; href?: string; avatarUrl?: string }[]> = {};
  combinedCredits.forEach(c => {
    if (!groupedCredits[c.role]) groupedCredits[c.role] = [];
    if (!groupedCredits[c.role].some((e: any) => e.name === c.name)) {
      groupedCredits[c.role].push({ name: c.name, href: c.href, avatarUrl: c.avatarUrl });
    }
  });

  // Home / Artists / <artist> / Releases / <release> [/ <track>]
  const artistLink = artistName && artistName !== t('unknownArtist')
    ? artistHref({ slug: item.artist?.slug, docId: item.artist?.docId, artistId: item.artistId }, artistName)
    : undefined;
  const releasesHref = artistLink ? `${artistLink}?tab=releases` : '/releases';
  const crumbs: Crumb[] = [
    ...(artistLink ? [
      { label: tNav('artists'), href: '/artists', icon: Users },
      { label: artistName, href: artistLink, icon: UserRound },
    ] : []),
    ...(isRelease || parentRelease ? [{ label: tNav('releases'), href: releasesHref, icon: Disc }] : []),
    ...(!isRelease && parentRelease ? [{ label: parentRelease.title, href: `/music/${parentRelease.id}`, icon: Disc3 }] : []),
    { label: item.title || '', icon: isRelease ? Disc3 : Music2 },
  ];
  const pageUrl = `${process.env.NEXT_PUBLIC_APP_URL || 'https://breakinbread.com'}/music/${resolvedParams.id}`;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Structured data for rich search results */}
      {isRelease ? (
        <MusicAlbumLD
          name={item.title || ''}
          artist={artistName}
          year={item.year}
          genre={Array.isArray(item.genres) ? item.genres[0] : item.genre}
          imageUrl={coverUrl}
          url={pageUrl}
          tracks={tracks.map((t: any, i: number) => ({
            name: t.title || `Track ${i + 1}`,
            position: t.trackNumber || i + 1,
            duration: t.durationMs,
          }))}
        />
      ) : (
        <MusicRecordingLD
          id={resolvedParams.id}
          name={item.title || ''}
          artist={artistName}
          duration={item.durationMs}
          genre={Array.isArray(item.genres) ? item.genres[0] : item.genre}
          imageUrl={coverUrl}
          url={pageUrl}
        />
      )}
      {/* Breadcrumb bar */}
      <div className="border-b border-border/50 bg-background/95 backdrop-blur sticky top-[50px] z-30">
        {/* Gordon 2026-08-10: space above the title cut 25% (12px → 9px) and
            the space below matched to it. "Below" was 20px, not 12 — the bar's
            own padding-bottom PLUS the content column's pt-2 stacked, which is
            why it read as lopsided. The column's top padding is now 0 so the
            bar alone owns the gap on both sides. */}
        <div className="w-full px-page pt-[9px] pb-[9px] flex items-center gap-3">
          <Breadcrumbs items={crumbs} className="min-w-0 flex-1" />
          {(isAdminMain || isSuperAdminMain) && item.id && (
            <LinkedSessionBadge entityId={item.id} />
          )}
          <div className="ml-auto">
            <AdminToolbar
              entityType={isRelease ? "Release" : "Track"}
              entityData={{
                ...item,
                // releaseDate may be a Firestore Timestamp — the edit dialog
                // needs a plain YYYY-MM-DD string, and saving a string back
                // is fine (every reader accepts Timestamp | ISO string).
                releaseDate: (
                  (item.releaseDate?.toDate?.()?.toISOString?.() as string | undefined)
                  || (typeof item.releaseDate === "string" ? item.releaseDate : "")
                ).slice(0, 10),
              }}
              isHidden={item.adminHidden}
              // Same owner rule as the page (owner.userId included) so the
              // cover's Edit pencil always opens this window for an owner.
              ownerId={isOwnerMain ? authUser?.uid : (item.owner?.userId || item.uploadedBy || item.createdBy || item.userId || item.artist?.createdBy)}
              editFields={[
                { key: "title", label: "Title", type: "text" },
                // type:"artist" → autocomplete that searches the artists
                // collection. Picking from the dropdown also drops the artist
                // doc id into `artistId` so the work links to the right doc.
                { key: "artistName", label: "Artist", type: "artist", artistIdField: "artistId" },
                ...(isRelease ? [{ key: "releaseType", label: "Release Type", type: "select" as const, options: [
                  { value: "album", label: "Album" },
                  { value: "ep", label: "EP" },
                  { value: "single", label: "Single" },
                  { value: "mixtape", label: "Mixtape" },
                  { value: "compilation", label: "Compilation" },
                  { value: "live album", label: "Live Album" },
                  // Demos are never official releases — picking one of these
                  // moves the release out of home "Releases" into "Demos".
                  { value: "demo", label: "Demo" },
                  { value: "demo compilation", label: "Demo Compilation" },
                ]}] : []),
                { key: "status", label: "Status", type: "select", options: [
                  { value: "released", label: "Released" },
                  { value: "unreleased", label: "Unreleased" },
                  { value: "archived", label: "Archived" },
                ]},
                // DB-backed multi-genre picker (comma string in the dialog;
                // converted to genres[] + genre in onUpdate below).
                { key: "genres", label: "Genres", type: "genre" },
                { key: "year", label: "Year", type: "year" },
                { key: "releaseDate", label: "Release Date", type: "date" },
                // Database-driven (Gordon 2026-11-03): the labels register first (sets labelId), then labels already used.
                { key: "label", label: "Label", type: "suggest", idField: "labelId", suggest: [
                  { collection: "labels", field: "name", withId: true },
                  { collection: "releases", field: "label" },
                ]},
                ...(isRelease
                  ? [{ key: "upc", label: "UPC / Barcode", type: "text" as const }]
                  : [{ key: "isrc", label: "ISRC", type: "text" as const }]),
                { key: "copyright", label: "Copyright (©)", type: "suggest", suggest: [{ collection: "releases", field: "copyright" }, { collection: "works", field: "copyright" }] },
                { key: "publisher", label: "Publisher", type: "suggest", suggest: [{ collection: "releases", field: "publisher" }, { collection: "works", field: "publisher" }] },
                { key: "description", label: "Description", type: "textarea" },
                { key: "aiDisclosure", label: "AI Disclosure & Authorship", type: "ai_disclosure" },
              ]}
              onToggleVisibility={async (hidden) => toggleReleaseAdminHidden(item.id || resolvedParams.id, hidden)}
              onUpdate={async (updateData) => {
                const payload: Record<string, any> = { ...updateData };
                if (payload.artistName !== undefined) {
                  payload.artist = payload.artistName;
                  if (item.artist && typeof item.artist === 'object') {
                    payload['artist.name'] = payload.artistName;
                  }
                }
                if (typeof payload.genres === 'string') {
                  const list = payload.genres.split(',').map((s: string) => s.trim()).filter(Boolean);
                  payload.genres = list;
                  payload.genre = list[0] || '';
                }
                if (payload.year !== undefined && payload.year !== '') {
                  const y = parseInt(String(payload.year), 10);
                  if (!isNaN(y)) payload.year = y; else delete payload.year;
                }
                if (payload.aiDisclosure) {
                  payload.aiLevel = payload.aiDisclosure.level || '0% AI';
                  payload.aiPercent = payload.aiDisclosure.aiPercent ?? 0;
                  payload.compositionHuman = payload.aiDisclosure.compositionHuman ?? true;
                }
                return isRelease
                  ? updateRelease(item.id || resolvedParams.id, payload)
                  : updateMedia(item.id || resolvedParams.id, payload);
              }}
              onDelete={async () => isRelease
                ? deleteRelease(item.id || resolvedParams.id)
                : deleteMedia(item.id || resolvedParams.id)}
              onDeleteRedirect={() => router.push('/home')}
            />
          </div>
        </div>
      </div>

      {/* ── Desktop 50/50 split layout ── */}
      <div className="flex flex-col md:flex-row md:h-[calc(100vh_-_var(--header-height)_-_43px)] md:overflow-hidden">

        {/* ── LEFT PANEL: cover art + metadata ── */}
        {/* Horizontal padding matches the breadcrumb (px-4 sm:px-6 md:px-8)
            so the thumbnail's left edge lines up with the "Release / title"
            row — Gordon: "all content left aligned the same". Top padding
            halved (pt-2) to tighten the gap between the breadcrumb and the
            cover. */}
        <div className={cn("md:w-[45%] md:min-h-0 md:overflow-y-auto md:border-r md:border-border/40 px-page pt-0 md:pt-0 pb-4 md:pb-5 flex flex-col gap-3", isPlayerBarActive && "pb-[80px] md:pb-24")}>
          {/* Cover art — full column width, LEFT-aligned so its left edge lines
              up with the breadcrumb and every metadata row below it (Gordon:
              "all content left aligned the same ... looks messy"). Was
              w-[95%] mx-auto (centred), which broke the alignment. */}
          <div
            className="relative w-full aspect-square rounded-2xl overflow-hidden shadow-2xl bg-muted group flex-shrink-0 cursor-zoom-in"
            onClick={() => coverImages.length > 0 && setLightboxIdx(0)}
          >
              {/* Audio-reactive ZOOM layer — the artwork gently breathes with
                  the music (reads the global VU level, works on iOS too). For
                  ALL users, no gate. */}
              <AudioReactiveCover className="absolute inset-0">
                {coverImages.length > 1 ? (
                  <SlideshowImage images={coverImages} alt={item.title || 'Album'} priority interactive />
                ) : coverUrl ? (
                  <Image src={coverUrl} alt={item.title || 'Album'} fill className="object-cover" priority />
                ) : (
                  <GenerativeArtistAvatar
                    name={artistName}
                    size={400}
                    className="absolute inset-0 w-full h-full"
                    animate
                    analyserNode={analyserL}
                  />
                )}
              </AudioReactiveCover>
              {/* AI mark — top-left, like every cover (Gordon 2026-10-31). */}
              <CoverAiSticker level={item.aiDisclosure?.level || item.aiLevel} className="z-20" />
              {/* Programmable animated FX (off by default) + draggable text
                  layers — designed on-cover by super-admins/creators, shown to
                  everyone. Fields: artworkFx / artworkFxIntensity /
                  artworkFxColor / coverTextLayers on the work|release doc. */}
              <CoverDesigner
                onEdit={() => window.dispatchEvent(new CustomEvent("bb-open-entity-edit", { detail: { id: item.id || resolvedParams.id } }))}
                editLabel={tCommon("edit")}
                mediaId={item.id || resolvedParams.id}
                collectionName={item.type === 'work' ? 'works' : 'releases'}
                canEdit={canManageArt || isOwnerMain}
                coverUrl={coverUrl}
                initialFx={{
                  effect: (item as any).artworkFx || 'off',
                  intensity: (item as any).artworkFxIntensity ?? 1,
                  color: (item as any).artworkFxColor || '',
                }}
                initialTextLayers={Array.isArray((item as any).coverTextLayers) ? (item as any).coverTextLayers : []}
                initialImageLayers={Array.isArray((item as any).coverImageLayers) ? (item as any).coverImageLayers : []}
              />
              {/* Parental Advisory regulation sticker — bottom-left, explicit only. */}
              {(item as any).explicit && (
                <div className="absolute bottom-2 left-2 z-[2] w-[34%] max-w-[132px] min-w-[68px] pointer-events-none">
                  <ParentalAdvisoryLabel width={132} className="w-full h-auto drop-shadow-lg" title="Parental Advisory — Explicit Content" />
                </div>
              )}
              {coverImages.length > 1 && (
                <div className="absolute top-2 left-2 z-10 bg-black/60 text-white text-xs px-2 py-1 rounded-full pointer-events-none">
                  {coverImages.length} images
                </div>
              )}
              {/* Artwork upload overlay for admins/creators */}
              {canManageArt && (
                <>
                  <input
                    ref={artInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleArtworkUpload}
                  />
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/0 group-hover:bg-black/60 transition-colors">
                    {uploadingArt ? (
                      <Loader2 className="h-8 w-8 text-white animate-spin" />
                    ) : (
                      <div className="bb-hover-reveal transition-opacity flex flex-col items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); artInputRef.current?.click(); }}
                          className="flex flex-col items-center gap-1 text-white hover:text-white/80 transition-colors"
                          title="Upload artwork (multi-select supported)"
                        >
                          <Upload className="h-7 w-7" />
                          <span className="text-xs font-medium">Upload</span>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); openArtVault(); }}
                          className="flex flex-col items-center gap-1 text-white hover:text-white/80 transition-colors"
                          title="Choose from media vault"
                        >
                          <ImagePlus className="h-7 w-7" />
                          <span className="text-xs font-medium">From Vault</span>
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
          </div>

          {/* Artwork strip — every picture of the release; the first is the
              cover. Admins hold + drag to reorder (touch and mouse). */}
          <ArtworkStrip
            images={coverImages}
            canManage={canManageArt}
            uploading={uploadingArt}
            onOpen={(i) => setLightboxIdx(i)}
            onRemove={(url) => { removeArtworkUrl(url); }}
            onOrder={(urls) => { setArtworkOrder(urls).catch(err => console.error('[art-order]', err)); }}
            onAdd={() => artInputRef.current?.click()}
          />

          {/* Videos — primary `videoUrl` plus additional `videos[]` URLs (YouTube
              or direct). Admins/creators get an inline manager: add by URL, remove,
              set as primary. Visitors see every video as an embedded iframe. */}
          {(item.videoUrl || (Array.isArray(item.videos) && item.videos.filter(Boolean).length > 0) || canManageArt) && (
            // order-last: the left column is a flex-col, so this drops the
            // Videos block below the info + action buttons (Gordon: "the
            // video url part should come after the info and buttons").
            <div className="flex-shrink-0 order-last">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <Video className="h-3.5 w-3.5" /> Videos
                </span>
                {(() => {
                  const total = (item.videoUrl ? 1 : 0) + (Array.isArray(item.videos) ? item.videos.filter(Boolean).length : 0);
                  return total > 0 ? (
                    <span className="text-xs text-muted-foreground">
                      {total} {total === 1 ? 'video' : 'videos'}
                    </span>
                  ) : null;
                })()}
              </div>

              {/* All videos, primary first */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  ...(item.videoUrl ? [{ url: item.videoUrl as string, isPrimary: true }] : []),
                  ...((Array.isArray(item.videos) ? item.videos.filter(Boolean) : []) as string[])
                    .map((u: string) => ({ url: u, isPrimary: false })),
                ].map(({ url, isPrimary }, i) => {
                  const ytId = extractVideoId(url);
                  return (
                    <div key={`${url}-${i}`} className="space-y-1">
                      <div className="relative aspect-video rounded-lg overflow-hidden bg-black group">
                        {ytId ? (
                          <iframe
                            src={`https://www.youtube.com/embed/${ytId}?modestbranding=1&rel=0`}
                            title={`Video ${i + 1}`}
                            className="absolute inset-0 w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : (
                          <video src={url} controls className="absolute inset-0 w-full h-full object-contain bg-black" />
                        )}
                        {canManageArt && (
                          <div className="absolute top-1.5 right-1.5 z-10 bb-hover-reveal transition-opacity flex gap-1">
                            {!isPrimary && (
                              <button
                                onClick={() => promoteReleaseVideoToPrimary(url)}
                                disabled={savingVideo}
                                className="bg-primary text-primary-foreground text-[10px] px-2 py-0.5 rounded-full shadow-md hover:bg-primary/80"
                                title="Set as primary video"
                              >
                                Set primary
                              </button>
                            )}
                            <button
                              onClick={() => removeReleaseVideo(url)}
                              disabled={savingVideo}
                              className="bg-destructive text-white text-[10px] px-2 py-0.5 rounded-full shadow-md hover:bg-destructive/80 flex items-center gap-1"
                              title="Remove video"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                        {isPrimary && (
                          <div className="absolute top-1.5 left-1.5 z-10 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded-full">
                            Primary
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Admin: add new video by URL */}
              {canManageArt && (
                <div className="flex gap-2 mt-2">
                  <Input
                    type="url"
                    placeholder="YouTube URL or direct video link"
                    value={videoDraft}
                    onChange={(e) => setVideoDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addReleaseVideo(); } }}
                    className="h-9 text-sm flex-1"
                    disabled={savingVideo}
                  />
                  <Button
                    size="sm"
                    onClick={addReleaseVideo}
                    disabled={!videoDraft.trim() || savingVideo}
                    className="h-9 gap-1.5"
                  >
                    {savingVideo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlusCircle className="h-3.5 w-3.5" />}
                    Add
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Metadata + action buttons (left panel) — always visible, never pushed off screen */}
          <div className="space-y-2 flex-shrink-0">
            <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
              {isRelease && <Badge variant="outline" className="capitalize">{item.releaseType || 'Album'}</Badge>}
              {shownDate && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{shownDate}</span>}
              {item.genre && <Badge variant="secondary">{item.genre}</Badge>}
              {tracks.length > 0 && <span>{tracks.length} tracks</span>}
              {item.totalDurationMs && <span>{formatDuration(item.totalDurationMs)}</span>}
            </div>
            {/* [&_button]:h-9 — every action in this row renders at the same
                height regardless of each component's own size default
                (Gordon: "make sure all buttons on same row have the same
                height"). */}
            {/* ONE row of icon buttons (Gordon 2026-11-04: "just heart icon for
                save, share as well. make them all icons only … and on 1 row");
                a phone swipes it. Every one keeps its name as aria-label/title. */}
            <div className="flex flex-nowrap items-center gap-2 overflow-x-auto hide-scrollbar [&_button]:h-9 [&_button]:min-w-9 [&_button]:shrink-0">
              <Button
                size="icon"
                aria-label={isRelease ? "Play release" : "Play"}
                title={isRelease ? "Play release" : "Play"}
                onClick={handlePlayAll}
                data-help-title={isRelease ? "Play release" : "Play track"}
                data-help-description={isRelease
                  ? "Queue every track on this release and start playback from track 1. New tracks replace your current queue."
                  : "Start playing this track in the BB media player."}
                data-help-anchor="media-player"
              ><Play className="h-4 w-4" /></Button>
              {/* Download comes right after Play (Gordon 2026-09-21). */}
              {isRelease ? (
                /* Release: per-track download dialog */
                <ReleaseDownloadButton item={item} tracks={tracks} iconOnly />
              ) : (
                /* Individual track: direct download with PWYW/paid support */
                (item.allowDownloads || (tracks.length > 0 && tracks.some((t: any) => t.allowDownloads))) && (
                  <DownloadButton item={item} tracks={tracks} openOnEvent autoOpen={autoDownload} iconOnly />
                )
              )}
              <SaveButton
                itemId={item.id || resolvedParams.id}
                itemType="release"
                title={item.title || ''}
                subtitle={artistName}
                imageUrl={coverUrl}
                link={`/music/${item.id || resolvedParams.id}`}
                showLabel={false}
                className="w-9 px-0"
              />
              {/* Share + QR come BEFORE Add to Playlist (Gordon). */}
              <ShareButton
                iconOnly
                className="h-9 w-9"
                // Lead with the artist so the bold line on every share card
                // (and the OS share-sheet title on Web Share API) reads as the
                // artist's name — matches the OG metadata in layout.tsx.
                title={(artistName && artistName !== t('unknownArtist'))
                  ? `${artistName} — ${item.title}`
                  : item.title}
                onShare={() => trackMediaEvent({
                  type: 'share',
                  entityType: item.type === 'work' ? 'track' : 'release',
                  entityId: item.id,
                  entityTitle: item.title,
                  entityArtist: item.artistName || item.artist?.name,
                })}
              />
              <QRShareButton
                // Same "Artist — Title" rule as the regular ShareButton
                // above — the QR-code modal header line now leads with
                // the artist so a screenshot of the modal still tells
                // someone whose track it is.
                title={(artistName && artistName !== t('unknownArtist')) ? `${artistName} — ${item.title}` : item.title}
                imageUrl={coverUrl}
              />
              <AddToPlaylist
                track={{
                  trackId: item.id || resolvedParams.id,
                  title: item.title || '',
                  artist: artistName,
                  imageUrl: coverUrl,
                  audioUrl: item.audioUrl || item.url || '',
                  duration: item.duration,
                }}
                variant="icon"
                className="h-9 w-9 border border-input"
              />
              {/* Sponsor this track / release — PWYW, routed to the
                  uploader's Connect account, recorded as a sponsorship of
                  this entity. */}
              <SponsorButton
                kind="sponsor"
                entityType={isRelease ? "release" : "track"}
                entityId={item.id || resolvedParams.id}
                entityTitle={item.title || ''}
                recipientId={item.owner?.userId || item.uploadedBy || item.createdBy || item.userId || ''}
                recipientName={artistName !== t('unknownArtist') ? artistName : (item.title || '')}
                coverArtUrl={coverUrl}
                label="Angel"
                compact
                className="h-9 w-9 rounded-md"
              />
              {/* Vinyl / cassette / CD / DVD — hidden when no pressing exists.
                  Visible to logged-out fans too; they're sent to /signin on Buy. */}
              <BuyPhysicalButton releaseId={item.id || resolvedParams.id} />
              {/* Sync licensing — ANY track can be licensed for film/TV/ads.
                  Opens the same inquiry flow as /synchronization; the inquiry
                  reaches the maker + BB by email, no signup needed. */}
              <Button
                size="icon"
                variant="outline"
                aria-label="License"
                title="License this track"
                onClick={() => setLicenseOpen(true)}
                data-help-title="License this track"
                data-help-description="Request a sync licence to use this music in a film, series, commercial, game or broadcast. Your inquiry goes straight to the artist — no account needed."
              >
                <Clapperboard className="h-4 w-4" />
              </Button>
              <ClaimCreditsButton
                iconOnly
                mediaId={item.id || resolvedParams.id}
                mediaType={isRelease ? "release" : "work"}
                mediaTitle={item.title || ""}
                tracks={isRelease
                  ? (tracks || [])
                      .map((t: any) => ({
                        workId: t.workId || t.id,
                        title: t.title || "Untitled",
                        trackNumber: t.trackNumber,
                        audioUrl: t.audioUrl || t.mp3Url,
                        mp3Url: t.mp3Url,
                      }))
                      .filter((t: any) => t.workId)
                  : undefined}
              />
              {/* Project/session file (TE EP-133/EP-1320, DAW…) — PWYW */}
              <ProjectFileButton item={item} iconOnly />
            </div>
            <SyncInquiryDialog
              open={licenseOpen}
              onOpenChange={setLicenseOpen}
              track={{
                id: item.id || resolvedParams.id,
                title: item.title || "",
                artistName: artistName,
                makerUid: item.owner?.userId || item.uploadedBy || item.createdBy || item.userId || "",
              }}
            />
            {/* Copyright */}
            {item.copyrightHolder && (
              <div className="pt-2 text-xs text-muted-foreground space-y-0.5">
                {item.pLine && <p>{item.pLine}</p>}
                {item.cLine && <p>{item.cLine}</p>}
                <p>&copy; {item.copyrightYear || ''} {item.copyrightHolder}</p>
                {item.catalogNumber && <p>Cat. No: {item.catalogNumber}</p>}
              </div>
            )}

            {/* AI / provenance — the "Music Ingredients Label" (read-only for all) */}
            {item.aiDisclosure?.level && (
              <div className="mt-4">
                <MusicIngredientsLabel disclosure={item.aiDisclosure} trackTitle={item.title} />
              </div>
            )}
            {/* Inline WYSIWYG editor for the AI/label info (admin/creator). */}
            <AiLabelInlineEditor
              mediaId={item.id || resolvedParams.id}
              collectionName={item.type === 'work' ? 'works' : 'releases'}
              canEdit={canManageArt || isOwnerMain}
              initial={item.aiDisclosure}
              trackTitle={item.title}
              onSaved={(d) => setItem((prev: any) => (prev ? { ...prev, aiDisclosure: d } : prev))}
            />

            {/* Download Leaderboard */}
            {leaderboard.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border/50">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5 mb-2">
                  🏆 Top Bakers
                </p>
                <div className="space-y-1.5">
                  {leaderboard.slice(0, 5).map((entry, idx) => {
                    const currSymbol: Record<string, string> = { eur: '€', usd: '$', gbp: '£', sek: 'kr' };
                    const sym = currSymbol[entry.currency] || '€';
                    const medals = ['🥇', '🥈', '🥉', '4️⃣', '5️⃣'];
                    return (
                      <div key={entry.id} className="flex items-center justify-between gap-2 text-sm">
                        <span className="flex items-center gap-1.5">
                          <span>{medals[idx] || '·'}</span>
                          <span className={cn("font-medium", entry.isAnonymous && "text-muted-foreground italic")}>
                            {entry.buyerName || 'Anonymous'}
                          </span>
                        </span>
                        <span className="text-muted-foreground font-mono text-xs">
                          {sym}{(entry.amountPaid / 100).toFixed(2)}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {item.totalRaised > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Total raised: {(() => { const sym: Record<string,string> = {eur:'€',usd:'$',gbp:'£',sek:'kr'}; return sym[leaderboard[0]?.currency] || '€'; })()}{(item.totalRaised / 100).toFixed(2)}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT PANEL: title + tabs ── */}
        <div className={cn("md:flex-1 md:min-h-0 md:overflow-y-auto p-4 md:p-6 flex flex-col gap-4", isPlayerBarActive && "pb-[80px] md:pb-24")}>
          {/* Title + artist */}
          <div>
            <h1 className="capitalize text-2xl sm:text-3xl font-bold leading-tight inline-flex items-center gap-2 flex-wrap">
              {item.title}
              {(item as any).explicit && <ExplicitBadge explicit size={18} />}
            </h1>
            {/* Genres — chips for everyone; owner/admin get inline editing
                (DB-driven picker, multi-genre; saves genres[] + genre for
                backward compat on works AND releases). */}
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              {!genreEditOpen && (
                <>
                  {normGenreList(item.genres, item.genre).map((g: string) => (
                    <Badge key={g} variant="outline" className="text-xs">{g}</Badge>
                  ))}
                  {(canManageArt || isOwnerMain) && (
                    <Button
                      size="sm" variant="ghost" className="h-6 px-2 text-xs gap-1 text-muted-foreground hover:text-foreground"
                      onClick={() => { setGenreDraft(normGenreList(item.genres, item.genre)); setGenreEditOpen(true); }}
                    >
                      <Pencil className="h-3 w-3" />
                      {normGenreList(item.genres, item.genre).length ? "Edit genres" : "Add genres"}
                    </Button>
                  )}
                </>
              )}
              {genreEditOpen && (
                <div className="w-full max-w-md space-y-2">
                  <GenresTagInput value={genreDraft} onChange={setGenreDraft} />
                  <div className="flex gap-2">
                    <Button
                      size="sm" className="h-7 text-xs" disabled={savingGenres}
                      onClick={async () => {
                        setSavingGenres(true);
                        try {
                          const genres = genreDraft.filter(Boolean);
                          const patch = { genres, genre: genres[0] || "" };
                          if (isRelease) await updateRelease(item.id || resolvedParams.id, patch);
                          else await updateWork(item.id || resolvedParams.id, patch as any);
                          setItem((prev: any) => prev ? { ...prev, ...patch } : prev);
                          setGenreEditOpen(false);
                          toast({ title: "Genres updated" });
                        } catch (e: any) {
                          toast({ variant: "destructive", title: "Could not save genres", description: String(e?.message || e).slice(0, 140) });
                        } finally { setSavingGenres(false); }
                      }}
                    >
                      {savingGenres ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}Save
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setGenreEditOpen(false)} disabled={savingGenres}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
            {/* Primary artist link + optional "feat. X, Y".
                Always renders as a link — when no id/slug is stored on the
                release doc, we slugify the artist name and link there. The
                artists/[slug] route falls back to a name lookup, so the link
                resolves whether or not the slug is the artist's saved one. */}
            <p className="text-lg text-muted-foreground mt-1">
              {artistName && artistName !== t('unknownArtist') ? (
                <Link
                  href={`/artists/${
                    item.artist?.slug
                      || item.artist?.docId
                      || item.artistId
                      || artistName.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")
                  }`}
                  className="hover:text-primary transition-colors hover:underline">
                  {artistName}
                </Link>
              ) : (
                <span>{artistName}</span>
              )}
              {Array.isArray(item.featuredArtists) && item.featuredArtists.length > 0 && (
                <span className="text-base"> feat.{" "}
                  {item.featuredArtists.map((fa: any, i: number) => {
                    const name = typeof fa === "string" ? fa : (fa?.name || fa?.displayName || "");
                    if (!name) return null;
                    // Always link (same fallback as the primary artist): use a stored
                    // id/slug when present, else slugify the name — artists/[slug]
                    // resolves by name lookup either way.
                    const href = (typeof fa === "object" && (fa.slug || fa.artistId || fa.docId))
                      ? `/artists/${fa.slug || fa.artistId || fa.docId}`
                      : (typeof fa === "object" && fa.userId)
                        ? `/profile/${fa.userId}`
                        : `/artists/${name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`;
                    return (
                      <span key={i}>
                        {i > 0 && ", "}
                        <Link href={href} className="hover:text-primary transition-colors hover:underline">{name}</Link>
                      </span>
                    );
                  })}
                </span>
              )}
            </p>
          </div>

          {/* Tabs: Tracks | Credits | Lyrics | Web Info */}
          <div className="w-full">
            {/* Use a simple CSS tab pattern so it works without extra client state */}
            <MusicPageTabs
              item={item}
              tracks={tracks}
              onTracksChange={setTracks}
              isRelease={isRelease}
              artistName={artistName}
              resolvedId={item.id || resolvedParams.id}
              currentTrackIdx={currentTrackIdx}
              globalIsPlaying={globalIsPlaying}
              expandedLyricsTrackId={expandedLyricsTrackId}
              setExpandedLyricsTrackId={setExpandedLyricsTrackId}
              groupedCredits={groupedCredits}
              formatDuration={formatDuration}
              handlePlayTrack={handlePlayTrack}
            />
          </div>
        </div>
      </div>

      {/* ── You might also like ── genre-matched suggestions below the main content. */}
      {similarReleases.length > 0 && (
        <div className="px-page pb-16 pt-2 w-full">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <Disc className="h-4 w-4 text-primary" />You might also like
            </h3>
            <PerRowPicker value={similarPerRow} onChange={setSimilarPerRow} />
          </div>
          {/* Full-width body + the SAME tile size as the home page grid view
              (Gordon: 'thumbnails should be standard size as on home page,
              make it full width body'). Mirrors home/page.tsx grid-view column
              counts so tiles are standard size — NOT the cramped 10-col compact
              set — while the row still spans the full content width. */}
          <div className={cn("grid gap-3", PER_ROW_GRID[similarPerRow])}>
            {similarReleases.map((r: any) => (
              <TiltCard key={r.id}>
              <Link href={`/music/${r.id}`} className="group">
                <Card className="overflow-hidden border-border/50 hover:border-primary/30 transition-all hover:shadow-lg">
                  <div className="relative aspect-square rounded-t-xl overflow-hidden bg-muted">
                    {(r.coverArtUrl || r.imageUrl) ? (
                      <Image src={r.coverArtUrl || r.imageUrl} alt={r.title} fill
                        className="object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center"><Disc className="h-8 w-8 text-muted-foreground/20" /></div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                      <Play className="h-8 w-8 text-white" />
                    </div>
                    <CoverExplicitSticker explicit={r.explicit} />
                  </div>
                  <CardContent className="p-2.5">
                    <p className="text-xs font-medium truncate">{r.title}</p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {(typeof r.artist === 'string' ? r.artist : r.artist?.name) || r.artistName || 'Unknown'} · {formatDate(r.releaseDate)}
                    </p>
                  </CardContent>
                </Card>
              </Link>
              </TiltCard>
            ))}
          </div>
        </div>
      )}

      {/* ── Artwork lightbox ── Shown when a gallery thumb or the hero is clicked.
          Phones/tablets swipe; desktop has the arrow buttons and ←/→ keys. */}
      <Dialog open={lightboxIdx !== null} onOpenChange={(o) => !o && setLightboxIdx(null)}>
        <DialogContent className="max-w-4xl p-0 bg-black/95 border-none"
          onKeyDown={(e) => {
            if (coverImages.length < 2) return;
            if (e.key === 'ArrowLeft') setLightboxIdx((i) => (i === null ? 0 : (i - 1 + coverImages.length) % coverImages.length));
            if (e.key === 'ArrowRight') setLightboxIdx((i) => (i === null ? 0 : (i + 1) % coverImages.length));
          }}>
          <DialogHeader className="sr-only">
            <DialogTitle>Artwork {(lightboxIdx ?? 0) + 1} of {coverImages.length}</DialogTitle>
          </DialogHeader>
          {lightboxIdx !== null && coverImages[lightboxIdx] && (
            <div className="relative w-full aspect-square sm:aspect-auto sm:h-[80vh] overflow-hidden"
              style={{ touchAction: coverImages.length > 1 ? 'pan-y' : undefined }}
              onTouchStart={(e) => { lbTouch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }}
              onTouchMove={(e) => {
                const t0 = lbTouch.current;
                if (!t0 || coverImages.length < 2) return;
                const dx = e.touches[0].clientX - t0.x;
                if (Math.abs(dx) <= Math.abs(e.touches[0].clientY - t0.y)) return;
                // Past the first or last picture the strip only gives a little.
                const edge = (lightboxIdx === 0 && dx > 0) || (lightboxIdx === coverImages.length - 1 && dx < 0);
                setLbDrag(edge ? dx * 0.3 : dx);
              }}
              onTouchEnd={(e) => {
                const dx = lbDrag;
                const w = (e.currentTarget as HTMLElement).clientWidth || 360;
                lbTouch.current = null;
                setLbDrag(0);
                if (coverImages.length < 2 || Math.abs(dx) < Math.min(60, w * 0.15)) return;
                setLightboxIdx((i) => {
                  const next = (i ?? 0) + (dx < 0 ? 1 : -1);
                  return next < 0 || next >= coverImages.length ? i : next;
                });
              }}
              onTouchCancel={() => { lbTouch.current = null; setLbDrag(0); }}>
              {/* The pictures lie side by side on a strip that follows the finger
                  and glides on to the next one (Gordon 2026-11-04: "really smooth …
                  glides over from one to the other"). Only the pictures next to
                  the one on show are loaded. */}
              <div className="absolute inset-0 flex will-change-transform"
                style={{ transform: `translateX(calc(${-lightboxIdx * 100}% + ${lbDrag}px))`, transition: lbDrag ? 'none' : 'transform 460ms cubic-bezier(.22,.8,.3,1)' }}>
                {coverImages.map((src, i) => (
                  <div key={`${i}-${src}`} className="relative h-full w-full shrink-0">
                    {Math.abs(i - lightboxIdx) <= 1 && (
                      <Image src={src} alt={`Artwork ${i + 1}`} fill className="object-contain" priority={i === lightboxIdx} sizes="(min-width: 640px) 56rem, 100vw" />
                    )}
                  </div>
                ))}
              </div>
              {coverImages.length > 1 && (
                <>
                  <button
                    onClick={() => setLightboxIdx((i) => (i === null ? 0 : (i - 1 + coverImages.length) % coverImages.length))}
                    className="touch:hidden absolute left-2 top-1/2 -translate-y-1/2 bg-white text-black hover:bg-white/90 rounded-full w-10 h-10 flex items-center justify-center shadow-lg"
                    aria-label="Previous artwork"
                  >
                    ‹
                  </button>
                  <button
                    onClick={() => setLightboxIdx((i) => (i === null ? 0 : (i + 1) % coverImages.length))}
                    className="touch:hidden absolute right-2 top-1/2 -translate-y-1/2 bg-white text-black hover:bg-white/90 rounded-full w-10 h-10 flex items-center justify-center shadow-lg"
                    aria-label="Next artwork"
                  >
                    ›
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1 rounded-full">
                    {lightboxIdx + 1} / {coverImages.length}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Art Vault Picker (full screen) ── */}
      <VaultArtworkPicker
        open={artVaultOpen}
        onOpenChange={setArtVaultOpen}
        loading={loadingVault}
        images={vaultImages}
        currentCover={item?.coverArtUrl || item?.imageUrl || ''}
        onPick={async (url) => {
          setArtVaultOpen(false);
          setUploadingArt(true);
          try { await applyArtworkUrl(url); }
          catch (err) { console.error('[vault-apply]', err); }
          finally { setUploadingArt(false); }
        }}
      />
    </div>
  );
}

// ─── Tabs sub-component (keeps parent as a server/client boundary) ────────────
function MusicPageTabs({
  item, tracks, onTracksChange, isRelease, artistName, resolvedId,
  currentTrackIdx, globalIsPlaying,
  expandedLyricsTrackId, setExpandedLyricsTrackId,
  groupedCredits, formatDuration, handlePlayTrack,
}: {
  item: any; tracks: any[];
  /** Syncs the parent MusicPage's own `tracks` state after an edit-mode
   *  reorder/remove/save — without this, the parent's stale `tracks` prop
   *  fed straight back into localTracks the moment Editing was toggled off,
   *  silently reverting the edit (see persistTracks/saveTracks below). */
  onTracksChange: (next: any[]) => void;
  isRelease: boolean; artistName: string; resolvedId: string;
  currentTrackIdx: number | null; globalIsPlaying: boolean;
  expandedLyricsTrackId: string | null; setExpandedLyricsTrackId: (id: string | null) => void;
  groupedCredits: Record<string, { name: string; href?: string; avatarUrl?: string }[]>;
  formatDuration: (ms: number) => string;
  handlePlayTrack: (idx: number, track: any) => void;
}) {
  const t = useTranslations('music');
  const tLyrics = useTranslations('lyrics');
  const tCommon = useTranslations('common');
  const tPlayer = useTranslations('player');
  const { user } = useAuth();
  const { toast } = useToast();
  const { currentTime: playerCurrentTime, currentTrack: playerCurrentTrack, isActive: playerIsActive } = useMediaPlayer();

  // Derived from item (available in scope here)
  const coverUrl = item.coverArtUrl || item.imageUrl || "";

  // ── Comments state ──────────────────────────────────────────────────────────
  const [comments, setComments] = useState<TrackComment[]>([]);
  const [newCommentText, setNewCommentText] = useState('');
  const [pinToTime, setPinToTime] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  // ── Waveform toggle + resizable height ─────────────────────────────────────
  const [showWaveform, setShowWaveform] = useState(false);
  const WAVEFORM_HEIGHT_KEY = 'bb-waveform-height';
  const [waveformHeight, setWaveformHeight] = useState<number>(() => {
    if (typeof window === 'undefined') return 72;
    return Math.max(40, Math.min(250, parseInt(localStorage.getItem(WAVEFORM_HEIGHT_KEY) ?? '72') || 72));
  });
  const waveformResizeRef = useRef<{ startY: number; startH: number } | null>(null);
  const handleWaveformResizeStart = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    waveformResizeRef.current = { startY: e.clientY, startH: waveformHeight };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const handleWaveformResizeMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!waveformResizeRef.current || e.buttons === 0) return;
    const delta = e.clientY - waveformResizeRef.current.startY;
    const next = Math.max(40, Math.min(250, waveformResizeRef.current.startH + delta));
    setWaveformHeight(next);
    try { localStorage.setItem(WAVEFORM_HEIGHT_KEY, String(Math.round(next))); } catch {}
  };
  const handleWaveformResizeEnd = () => { waveformResizeRef.current = null; };

  // ── Edit mode (admin/superadmin) ─────────────────────────────────────────────
  const { isAdmin, isSuperAdmin } = useAdmin();
  const canEdit = isAdmin || isSuperAdmin;
  const [editMode, setEditMode] = useState(false);

  // Deep link: ?tab=tracks|lyrics|credits|analysis|web|comments picks the tab
  // (contributor notifications already link `?tab=credits`), ?edit=1 starts
  // edit mode for admins — BB Copilot's openEditor for releases.
  const [activeTab, setActiveTab] = useState<string>(isRelease ? 'tracks' : 'lyrics');
  const [wantEdit, setWantEdit] = useState(false);
  useEffect(() => {
    const TABS = ['tracks', 'lyrics', 'credits', 'analysis', 'web', 'comments'];
    const apply = (search: string) => {
      const p = new URLSearchParams(search);
      const tab = p.get('tab') || '';
      if (TABS.includes(tab) && (tab !== 'tracks' || isRelease)) setActiveTab(tab);
      if (p.get('edit') === '1') setWantEdit(true);
    };
    apply(window.location.search);
    const onOpen = (e: Event) => {
      try {
        const u = new URL(String((e as CustomEvent<{ url?: string }>).detail?.url || ''), window.location.origin);
        if (u.pathname === window.location.pathname) apply(u.search);
      } catch { /* not a URL */ }
    };
    window.addEventListener('bb-open-editor', onOpen);
    return () => window.removeEventListener('bb-open-editor', onOpen);
  }, [isRelease]);
  useEffect(() => {
    if (wantEdit && canEdit) { setEditMode(true); setWantEdit(false); }
  }, [wantEdit, canEdit]);

  // Local editable tracks
  const [localTracks, setLocalTracks] = useState<any[]>(tracks);
  // The playing row follows the PLAYER's track, so playback started on Home
  // (or anywhere) shows here too (Gordon 2026-10-31); else this page's pick.
  const playingIdx: number | null = (() => {
    const pid = playerCurrentTrack?.id;
    if (!pid) return currentTrackIdx;
    const at = localTracks.findIndex((t: any) => (t.workId || t.id) === pid || t.id === pid);
    return at >= 0 ? at : null;
  })();
  const [isSavingTracks, setIsSavingTracks] = useState(false);

  // ── Admin: fetch missing track durations from audio URLs (client-side) ────
  const [fetchingDurations, setFetchingDurations] = useState(false);
  const handleFetchDurations = async () => {
    const missing = localTracks.filter(t => !t.durationMs && (t.audioUrl || t.mp3Url));
    if (missing.length === 0) { return; }
    setFetchingDurations(true);
    const updated = [...localTracks];
    for (const track of missing) {
      const url = track.mp3Url || track.audioUrl;
      try {
        const durationMs = await new Promise<number>((resolve) => {
          const audio = new Audio();
          audio.preload = 'metadata';
          audio.addEventListener('loadedmetadata', () => resolve(isFinite(audio.duration) ? Math.round(audio.duration * 1000) : 0));
          audio.addEventListener('error', () => resolve(0));
          audio.src = url;
        });
        if (durationMs > 0 && track.workId) {
          await updateDoc(doc(db, 'works', track.workId), { durationMs });
          const idx = updated.findIndex(t => t.workId === track.workId || t.id === track.id);
          if (idx !== -1) updated[idx] = { ...updated[idx], durationMs };
        }
      } catch { /* skip */ }
    }
    setLocalTracks(updated);
    setFetchingDurations(false);
  };
  const [addTrackExpanded, setAddTrackExpanded] = useState(false);
  const [trackSearch, setTrackSearch] = useState('');
  const [trackSearchResults, setTrackSearchResults] = useState<any[]>([]);
  const [isSearchingTracks, setIsSearchingTracks] = useState(false);
  const [editingLyricsIdx, setEditingLyricsIdx] = useState<number | null>(null);
  const [editingAudioIdx, setEditingAudioIdx] = useState<number | null>(null);
  // Per-track video editor: null = collapsed; index = which row is open.
  const [editingVideosIdx, setEditingVideosIdx] = useState<number | null>(null);
  const [trackVideoDraft, setTrackVideoDraft] = useState("");
  const [savingTrackVideo, setSavingTrackVideo] = useState(false);

  // Local editable credits
  const [localCredits, setLocalCredits] = useState<{role:string;name:string;href?:string}[]>(() =>
    Object.entries(groupedCredits).flatMap(([role, people]) =>
      (people as any[]).map((p: any) => ({ role, name: p.name, href: p.href }))
    )
  );
  const [newCreditRole, setNewCreditRole] = useState('');
  const [newCreditName, setNewCreditName] = useState('');
  const [newCreditUserId, setNewCreditUserId] = useState('');
  const [isSavingCredits, setIsSavingCredits] = useState(false);

  // Enrich credits that lack hrefs by looking up names against artists + users collections
  const creditsEnrichedRef = useRef(false);
  useEffect(() => {
    if (creditsEnrichedRef.current) return;
    const unlinked = localCredits.filter(c => !c.href);
    if (unlinked.length === 0) { creditsEnrichedRef.current = true; return; }
    creditsEnrichedRef.current = true;
    const uniqueNames = [...new Set(unlinked.map(c => c.name))];
    // Chunk into groups of 10 for Firestore 'in' queries
    const chunks: string[][] = [];
    for (let i = 0; i < uniqueNames.length; i += 10) chunks.push(uniqueNames.slice(i, i + 10));
    (async () => {
      const nameHrefMap = new Map<string, string>();
      // 1. Look up artists by name
      for (const chunk of chunks) {
        try {
          const snap = await getDocs(query(collection(db, 'artists'), where('name', 'in', chunk)));
          snap.forEach(d => {
            const data = d.data();
            if (data.name) nameHrefMap.set(data.name, `/artists/${data.slug || d.id}`);
          });
        } catch { /* non-critical */ }
      }
      // 2. For names still unmatched, look up users by displayName
      const stillUnmatched = uniqueNames.filter(n => !nameHrefMap.has(n));
      for (let i = 0; i < stillUnmatched.length; i += 10) {
        const chunk = stillUnmatched.slice(i, i + 10);
        try {
          const snap = await getDocs(query(collection(db, 'users'), where('displayName', 'in', chunk)));
          snap.forEach(d => {
            const data = d.data();
            if (data.displayName) nameHrefMap.set(data.displayName, `/profile/${d.id}`);
          });
        } catch { /* non-critical */ }
      }
      if (nameHrefMap.size > 0) {
        setLocalCredits(prev => prev.map(c =>
          (!c.href && nameHrefMap.has(c.name)) ? { ...c, href: nameHrefMap.get(c.name) } : c
        ));
      }
    })();
  }, [localCredits.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync local tracks from props when not editing
  useEffect(() => {
    if (!editMode) setLocalTracks(tracks);
  }, [tracks, editMode]);

  // Debounced vault track search
  useEffect(() => {
    if (!trackSearch.trim()) { setTrackSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setIsSearchingTracks(true);
      try {
        const q = query(collection(db, 'works'), orderBy('title'), limit(200));
        const snap = await getDocs(q);
        const lower = trackSearch.toLowerCase();
        setTrackSearchResults(
          snap.docs.map(d => ({ id: d.id, ...d.data() }))
            .filter((w: any) => (w.title || '').toLowerCase().includes(lower))
            .slice(0, 8)
        );
      } catch { setTrackSearchResults([]); }
      finally { setIsSearchingTracks(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [trackSearch]);

  // Persists a reordered/trimmed track list to Firestore AND syncs `tracks`
  // (the source-of-truth state re-derived from loadMusic()) in the same
  // motion as `localTracks`. Previously removeTrack/moveTrackUp/moveTrackDown
  // only ever touched localTracks — nothing was written to Firestore, and
  // the very next "sync local tracks from props when not editing" effect
  // (tied to `tracks`, which was never updated) silently reverted the whole
  // edit the moment Editing was toggled off. Gordon 2026-09-21^H26:
  // "I tried to erase a track in super admin release but it comes back
  // after I erase it from list in editing mode."
  const persistTracks = async (next: any[]) => {
    setLocalTracks(next);
    onTracksChange(next);
    try {
      const toSave = next.map((t, i) => ({
        title: t.title || '', trackNumber: i + 1,
        audioUrl: t.audioUrl || '', lyrics: t.lyrics || '',
        durationMs: t.durationMs || 0,
        artistName: t.artistName || t.artist?.name || '',
        workId: t.workId || t.id || '',
        videoUrl: t.videoUrl || '',
        videos: Array.isArray(t.videos) ? t.videos.filter(Boolean) : [],
      }));
      await updateRelease(resolvedId, { tracks: toSave });
    } catch (e) {
      console.error('persistTracks error:', e);
      toast({ variant: 'destructive', title: 'Could not save track order', description: 'Your change may not have been saved — try again.' });
    }
  };
  const moveTrackUp = (idx: number) => {
    if (idx === 0) return;
    const t = [...localTracks];
    [t[idx - 1], t[idx]] = [t[idx], t[idx - 1]];
    void persistTracks(t.map((tk, i) => ({ ...tk, trackNumber: i + 1 })));
  };
  const moveTrackDown = (idx: number) => {
    if (idx === localTracks.length - 1) return;
    const t = [...localTracks];
    [t[idx], t[idx + 1]] = [t[idx + 1], t[idx]];
    void persistTracks(t.map((tk, i) => ({ ...tk, trackNumber: i + 1 })));
  };
  const removeTrack = (idx: number) => {
    const removedTitle = localTracks[idx]?.title;
    void persistTracks(localTracks.filter((_, i) => i !== idx).map((tk, i) => ({ ...tk, trackNumber: i + 1 })));
    toast({ title: 'Track removed', description: removedTitle ? `"${removedTitle}" taken off the release.` : undefined });
  };
  const updateTrackLyrics = (idx: number, lyrics: string) => {
    setLocalTracks(prev => prev.map((tk, i) => i === idx ? { ...tk, lyrics } : tk));
  };
  const updateTrackAudio = (idx: number, audioUrl: string) => {
    setLocalTracks(prev => prev.map((tk, i) => i === idx ? { ...tk, audioUrl } : tk));
  };

  // ── Per-track video CRUD ─────────────────────────────────────────────────
  // Writes through to BOTH the canonical works/{workId} doc (so the track
  // page sees the change) AND localTracks (so the release tracklist updates
  // immediately without waiting on a refetch). Also nudges the embedded
  // release.tracks[i] entry via saveTracks if the user later hits "Save".
  const addTrackVideo = useCallback(async (idx: number) => {
    const raw = trackVideoDraft.trim();
    if (!raw) return;
    const track = localTracks[idx];
    if (!track) return;
    const workId = track.workId || track.id;
    const existing: string[] = Array.isArray(track.videos) ? track.videos : [];
    if (existing.includes(raw) || track.videoUrl === raw) {
      setTrackVideoDraft("");
      return;
    }
    setSavingTrackVideo(true);
    try {
      // First video on a track = primary (videoUrl). Subsequent = videos[].
      const updates: Record<string, any> = !track.videoUrl
        ? { videoUrl: raw }
        : { videos: [...existing, raw] };
      if (workId) {
        await updateDoc(doc(db, 'works', workId), updates);
      }
      setLocalTracks(prev => prev.map((tk, i) => i === idx ? { ...tk, ...updates } : tk));
      setTrackVideoDraft("");
    } catch (err) {
      console.error('[track-video add]', err);
    } finally {
      setSavingTrackVideo(false);
    }
  }, [trackVideoDraft, localTracks]);

  const removeTrackVideo = useCallback(async (idx: number, url: string) => {
    const track = localTracks[idx];
    if (!track) return;
    const workId = track.workId || track.id;
    setSavingTrackVideo(true);
    try {
      let updates: Record<string, any> = {};
      if (track.videoUrl === url) {
        // Removing the primary — promote the first additional, if any.
        const rest: string[] = Array.isArray(track.videos) ? [...track.videos] : [];
        const nextPrimary = rest.shift() || "";
        updates = { videoUrl: nextPrimary, videos: rest };
      } else {
        const next: string[] = (Array.isArray(track.videos) ? track.videos : []).filter((u: string) => u !== url);
        updates = { videos: next };
      }
      if (workId) {
        await updateDoc(doc(db, 'works', workId), updates);
      }
      setLocalTracks(prev => prev.map((tk, i) => i === idx ? { ...tk, ...updates } : tk));
    } catch (err) {
      console.error('[track-video remove]', err);
    } finally {
      setSavingTrackVideo(false);
    }
  }, [localTracks]);
  const addTrackFromVault = (work: any) => {
    const next: any = {
      id: work.id, workId: work.id,
      title: work.title || 'Untitled',
      trackNumber: localTracks.length + 1,
      artistName: work.artist || work.artistName || artistName,
      artist: { name: work.artist || work.artistName || artistName },
      audioUrl: work.audioUrl || work.url || '',
      mp3Url: work.mp3Url || '',
      durationMs: work.durationMs || 0,
      lyrics: work.lyrics || '',
    };
    setLocalTracks(prev => [...prev, next]);
    setTrackSearch(''); setTrackSearchResults([]); setAddTrackExpanded(false);
  };
  const saveTracks = async () => {
    setIsSavingTracks(true);
    try {
      const toSave = localTracks.map((t, i) => ({
        title: t.title || '',
        // Always the current array position — never trust a stored
        // trackNumber. Trusting it let stale/duplicate values (e.g. from
        // ID3 tags on a bulk-uploaded compilation) survive a save and
        // render as paired duplicate numbers in the view-mode tracklist.
        trackNumber: i + 1,
        audioUrl: t.audioUrl || '', lyrics: t.lyrics || '',
        durationMs: t.durationMs || 0,
        artistName: t.artistName || t.artist?.name || '',
        workId: t.workId || t.id || '',
        // Denormalize per-track videos onto the release so the tracklist
        // can render the video-indicator without n+1 lookups against works.
        videoUrl: t.videoUrl || '',
        videos: Array.isArray(t.videos) ? t.videos.filter(Boolean) : [],
      }));
      await updateRelease(resolvedId, { tracks: toSave });
      // Keep `tracks` (the source-of-truth state loadMusic() populated) in
      // step — it was never updated here before, so the "sync local tracks
      // from props when not editing" effect below reverted this save the
      // moment Editing was toggled off.
      onTracksChange(toSave);
      toast({ title: 'Saved', description: 'Track order and lyrics updated.' });
    } catch (e) {
      console.error('Save tracks error:', e);
      toast({ variant: 'destructive', title: 'Could not save', description: 'Track order/lyrics were not saved — try again.' });
    }
    finally { setIsSavingTracks(false); }
  };
  const addCredit = async () => {
    if (!newCreditRole.trim() || !newCreditName.trim()) return;
    const name = newCreditName.trim();
    const role = newCreditRole.trim();
    // Dedupe/merge: if this person already credited (by userId, else name),
    // merge the role into the existing entry instead of adding a duplicate row.
    const norm = (s: string) => s.trim().toLowerCase();
    const idx = localCredits.findIndex((c: any) =>
      (newCreditUserId && c.userId === newCreditUserId) || (!newCreditUserId && norm(c.name) === norm(name)));
    let updated: any[];
    if (idx >= 0) {
      updated = localCredits.map((c: any, i: number) => {
        if (i !== idx) return c;
        const roles = Array.from(new Set([...(c.roles || (c.role ? [c.role] : [])), role]));
        return { ...c, roles, role: roles[0], userId: c.userId || newCreditUserId || undefined };
      });
    } else {
      updated = [...localCredits, { role, name, ...(newCreditUserId ? { userId: newCreditUserId } : {}) }];
    }
    setLocalCredits(updated);
    setNewCreditRole(''); setNewCreditName(''); setNewCreditUserId('');
    setIsSavingCredits(true);
    try { await updateRelease(resolvedId, { contributors: updated }); }
    catch (e) { console.error('Save credits error:', e); }
    finally { setIsSavingCredits(false); }
  };
  const removeCredit = async (idx: number) => {
    const updated = localCredits.filter((_, i) => i !== idx);
    setLocalCredits(updated);
    setIsSavingCredits(true);
    try { await updateRelease(resolvedId, { contributors: updated }); }
    catch (e) { console.error('Save credits error:', e); }
    finally { setIsSavingCredits(false); }
  };

  // Figure out which audio URL to show in the waveform
  const waveformAudioUrl: string = (() => {
    // If the global player is actively playing something from this page's tracks, use that
    if (playerIsActive && playerCurrentTrack?.audioUrl) {
      const matchingTrack = tracks.find((t: any) => t.audioUrl === playerCurrentTrack.audioUrl);
      if (matchingTrack) return playerCurrentTrack.audioUrl;
    }
    // Otherwise fall back to first available track audio
    if (!isRelease) return item.audioUrl || '';
    const first = tracks.find((t: any) => t.audioUrl);
    return first?.audioUrl || '';
  })();

  useEffect(() => {
    const unsub = subscribeTrackComments(resolvedId, setComments);
    return unsub;
  }, [resolvedId]);

  const handleSubmitComment = async () => {
    if (!user || !newCommentText.trim() || isSubmittingComment) return;
    setIsSubmittingComment(true);
    try {
      // Capture actual playback position when pinning to time
      const pinnedSeconds = pinToTime ? Math.floor(playerCurrentTime) : 0;
      await addTrackComment({
        trackId: resolvedId,
        userId: user.uid,
        userName: user.displayName || user.email?.split('@')[0] || 'User',
        userImageUrl: user.photoURL || undefined,
        content: newCommentText.trim(),
        timestampSeconds: pinnedSeconds,
      });
      setNewCommentText('');
    } catch (err) {
      console.error('Error adding comment:', err);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteTrackComment(resolvedId, commentId);
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };

  const handleCommentReaction = async (commentId: string, emoji: string, hasReacted: boolean) => {
    if (!user?.uid) return;
    try {
      if (hasReacted) {
        await removeTrackCommentReaction(resolvedId, commentId, user.uid, emoji);
      } else {
        await addTrackCommentReaction(resolvedId, commentId, user.uid, emoji);
      }
    } catch (err) {
      console.error('Error toggling reaction:', err);
    }
  };

  const formatTimeAgo = (ts: any) => {
    if (!ts) return '';
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    return `${diffD}d ago`;
  };

  return (
    <div className="w-full flex flex-col gap-3">
    {/* ── Waveform toggle strip ── */}
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setShowWaveform(v => !v)}
          className={cn(
            "flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors",
            showWaveform
              ? "border-foreground bg-foreground text-background"
              : "border-border text-muted-foreground hover:text-foreground hover:border-border/80"
          )}
        >
          <AudioLines className="h-3.5 w-3.5" />
          Waveform
        </button>
        {canEdit && (
          <button
            type="button"
            onClick={() => { setEditMode(v => !v); if (editMode) { setEditingLyricsIdx(null); setAddTrackExpanded(false); } }}
            className={cn(
              "flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors",
              editMode
                ? "border-foreground bg-foreground text-background"
                : "border-border text-muted-foreground hover:text-foreground hover:border-border/80"
            )}
          >
            <Pencil className="h-3 w-3" />
            {editMode ? 'Editing' : tCommon('edit')}
          </button>
        )}
      </div>
      {showWaveform && waveformAudioUrl && (
        <span className="text-[10px] text-muted-foreground">Click to seek · orange pins = comments · VU meters show real audio level</span>
      )}
    </div>

    {/* ── Waveform canvas ── */}
    {showWaveform && (
      <div className="rounded-lg border border-border/50 bg-secondary/20 p-3 select-none">
        {waveformAudioUrl ? (
          <>
            <TrackWaveform
              audioUrl={waveformAudioUrl}
              durationSeconds={
                isRelease
                  ? (tracks.find((t: any) => t.audioUrl === waveformAudioUrl)?.durationMs || 0) / 1000
                  : (item.durationMs || 0) / 1000
              }
              comments={comments.filter(c => !c.isGlobal)}
              height={waveformHeight}
              showVu={false}
            />
            {/* ── Vertical resize handle ── */}
            <div
              className="flex items-center justify-center h-4 mt-1 cursor-ns-resize opacity-30 hover:opacity-80 transition-opacity touch-none"
              onPointerDown={handleWaveformResizeStart}
              onPointerMove={handleWaveformResizeMove}
              onPointerUp={handleWaveformResizeEnd}
              onPointerCancel={handleWaveformResizeEnd}
              title="Drag to resize waveform"
            >
              <GripHorizontal className="h-3.5 w-24 text-muted-foreground" />
            </div>
          </>
        ) : (
          <p className="text-xs text-muted-foreground text-center py-4">
            No audio file available for waveform
          </p>
        )}
      </div>
    )}

    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="flex w-auto overflow-x-auto hide-scrollbar gap-0 justify-start p-0 h-auto">
        {isRelease && <TabsTrigger value="tracks">{t('tracks')}</TabsTrigger>}
        <TabsTrigger value="lyrics">{tLyrics('title')}</TabsTrigger>
        <TabsTrigger value="credits">{tPlayer('credits')}</TabsTrigger>
        <TabsTrigger value="analysis" className="flex items-center gap-1">
          <Activity className="h-3.5 w-3.5" />
          Analysis
        </TabsTrigger>
        <TabsTrigger value="web">{tPlayer('web')}</TabsTrigger>
        <TabsTrigger value="comments" className="self-stretch" aria-label={`Comments${comments.length > 0 ? ` (${comments.length})` : ''}`}>
          <span className="flex items-center gap-1">
            <MessageCircle className="h-3.5 w-3.5" />
            {comments.length > 0 && <span className="text-xs">{comments.length}</span>}
          </span>
        </TabsTrigger>
      </TabsList>

      {/* Tracks tab (albums only) */}
      {isRelease && (
        <TabsContent value="tracks" className="mt-4">
          {/* Admin toolbar for tracks tab */}
          {canEdit && localTracks.some(t => !t.durationMs && (t.audioUrl || t.mp3Url)) && (
            <div className="flex justify-end mb-2">
              <Button size="sm" variant="outline" onClick={handleFetchDurations} disabled={fetchingDurations} className="text-xs">
                {fetchingDurations ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Fetching…</> : <><Clock className="h-3 w-3 mr-1" />Fetch Durations</>}
              </Button>
            </div>
          )}
          {localTracks.length === 0 && !editMode ? (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">No tracks available</p>
              {canEdit && <p className="text-xs text-muted-foreground/60 mt-1">Click Edit above to add tracks</p>}
            </div>
          ) : (
            <div className="space-y-1">
              {localTracks.map((track: any, i: number) => (
                <div key={track.id || i} className="rounded-lg border border-transparent hover:border-border/40 transition-colors">
                  <div
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-secondary/50 transition-colors group cursor-pointer"
                    onClick={() => !editMode && handlePlayTrack(i, track)}
                  >
                    {/* Track number / play icon */}
                    {editMode ? (
                      // Edit mode used to disable click-to-play entirely (the
                      // row's own onClick is `!editMode && handlePlayTrack`),
                      // forcing Gordon to toggle Editing off just to preview a
                      // track before deciding to remove it. A dedicated button
                      // here keeps listening available without leaving edit
                      // mode (Gordon 2026-09-26: "in editing mode the play
                      // button dissappears ... please show it so one can
                      // listen and remove without have to click on
                      // Editing/Edit button each time to listen").
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handlePlayTrack(i, track); }}
                        className="w-6 h-6 flex items-center justify-center text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
                        title={playingIdx === i && globalIsPlaying ? "Pause" : "Play"}
                        data-tap-info={playingIdx === i && globalIsPlaying ? "Pause" : "Play"}
                      >
                        {playingIdx === i && globalIsPlaying
                          ? <Pause className="h-4 w-4 text-primary" />
                          : <Play className="h-4 w-4" />}
                      </button>
                    ) : (
                      <>
                        {/* Mobile has no real :hover, so the old "number by
                            default, icon on hover" swap left touch users with
                            no visible play affordance at all (Gordon: "not
                            visible until you click on it, mobile light mode").
                            Mobile always shows the icon; desktop keeps the
                            hover swap. */}
                        <span className="hidden sm:inline-block w-6 text-right text-xs text-muted-foreground sm:group-hover:hidden touch:!hidden">{i + 1}</span>
                        <span className="inline-block sm:hidden sm:group-hover:inline-block touch:!inline-block w-6 text-right">
                          {playingIdx === i && globalIsPlaying
                            ? <Pause className="h-4 w-4 text-primary" />
                            : <Play className="h-4 w-4 text-primary" />}
                        </span>
                      </>
                    )}

                    <div className="flex-1 min-w-0">
                      <MarqueeText text={track.title} className={`text-sm font-medium ${playingIdx === i ? 'text-primary' : ''}`} />
                      {(() => {
                        const tArtistId = track.artist?.docId || track.artistId || item?.artist?.docId || item?.artistId || '';
                        const tArtistName = track.artist?.name || track.artistName || artistName;
                        return tArtistId ? (
                          <Link href={`/artists/${tArtistId}`} onClick={e => e.stopPropagation()} className="text-xs text-muted-foreground truncate block hover:text-primary transition-colors hover:underline">{tArtistName}</Link>
                        ) : (
                          <p className="text-xs text-muted-foreground truncate">{tArtistName}</p>
                        );
                      })()}
                    </div>

                    {/* Duration / BPM / Key — compact, right-aligned */}
                    <div className="flex flex-col items-end gap-0.5 flex-shrink-0 text-right">
                      <span className="text-xs text-muted-foreground tabular-nums">{formatDuration(track.durationMs)}</span>
                      {(track.bpm || track.musicAnalysis?.bpm) && (
                        <span className="text-[9px] text-muted-foreground/60 leading-none">{Math.round(track.bpm || track.musicAnalysis.bpm)} BPM</span>
                      )}
                      {(track.key || track.musicAnalysis?.keyName) && (
                        <span className="text-[9px] text-muted-foreground/60 leading-none">{track.key || track.musicAnalysis.keyName}</span>
                      )}
                    </div>

                    {/* Lyrics toggle (view mode) */}
                    {!editMode && track.lyrics && (
                      <Button
                        variant="ghost" size="icon" className="h-7 w-7 opacity-50 hover:opacity-100"
                        onClick={(e) => { e.stopPropagation(); setExpandedLyricsTrackId(expandedLyricsTrackId === track.id ? null : track.id); }}
                        title="Show lyrics"
                        data-tap-info="Show lyrics"
                      >
                        <FileText className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {/* Has-video indicator (view mode) — clicking opens the
                        track's own page where videos play full-size. */}
                    {!editMode && (track.videoUrl || (Array.isArray(track.videos) && track.videos.length > 0)) && (track.workId || track.id) && (
                      <Link
                        href={`/music/${track.workId || track.id}`}
                        onClick={e => e.stopPropagation()}
                        className="h-7 w-7 inline-flex items-center justify-center text-muted-foreground/60 hover:text-primary transition-colors"
                        title={`${(track.videoUrl ? 1 : 0) + (Array.isArray(track.videos) ? track.videos.length : 0)} video(s) — open track page`}
                      >
                        <Video className="h-3.5 w-3.5" />
                      </Link>
                    )}

                    {/* No-audio warning (view mode, admins only) */}
                    {!editMode && canEdit && !track.audioUrl && (
                      <span title="No audio file linked to this track" className="flex-shrink-0">
                        <AlertCircle className="h-3.5 w-3.5 text-orange-500/70" />
                      </span>
                    )}

                    {/* Edit mode controls */}
                    {editMode && (
                      <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                        {/* Audio URL link toggle */}
                        <Button variant="ghost" size="icon"
                          className={cn("h-7 w-7", editingAudioIdx === i ? "text-primary" : !track.audioUrl ? "text-orange-500 hover:text-orange-400" : "text-muted-foreground/60 hover:text-foreground")}
                          title={track.audioUrl ? "Edit audio URL" : "Link audio file"}
                          data-tap-info={track.audioUrl ? "Edit audio URL" : "Link audio file"}
                          onClick={() => setEditingAudioIdx(editingAudioIdx === i ? null : i)}>
                          <LinkIcon className="h-3.5 w-3.5" />
                        </Button>
                        {/* Lyrics edit toggle */}
                        <Button variant="ghost" size="icon" className={cn("h-7 w-7", editingLyricsIdx === i ? "text-primary" : "text-muted-foreground/60 hover:text-foreground")}
                          title="Edit lyrics"
                          data-tap-info="Edit lyrics"
                          onClick={() => setEditingLyricsIdx(editingLyricsIdx === i ? null : i)}>
                          <FileText className="h-3.5 w-3.5" />
                        </Button>
                        {/* Per-track videos edit toggle — writes through to
                            works/{workId}.videoUrl + videos[] immediately. */}
                        <Button variant="ghost" size="icon"
                          className={cn("h-7 w-7", editingVideosIdx === i ? "text-primary" : (track.videoUrl || (Array.isArray(track.videos) && track.videos.length > 0)) ? "text-foreground/80 hover:text-foreground" : "text-muted-foreground/60 hover:text-foreground")}
                          title="Edit track videos"
                          data-tap-info="Edit videos"
                          onClick={() => { setEditingVideosIdx(editingVideosIdx === i ? null : i); setTrackVideoDraft(""); }}>
                          <Video className="h-3.5 w-3.5" />
                        </Button>
                        {/* Reorder */}
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground/60 hover:text-foreground" disabled={i === 0} onClick={() => moveTrackUp(i)} title="Move up" data-tap-info="Move up">
                          <ChevronUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground/60 hover:text-foreground" disabled={i === localTracks.length - 1} onClick={() => moveTrackDown(i)} title="Move down" data-tap-info="Move down">
                          <ChevronDown className="h-3.5 w-3.5" />
                        </Button>
                        {/* Remove */}
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive/60 hover:text-destructive" onClick={() => removeTrack(i)} title="Remove track" data-tap-info="Remove track">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}

                    {!editMode && (
                      <div className="flex items-center gap-0.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                        {track.audioUrl && (
                          <>
                            <Volume2 className="hidden sm:block h-3.5 w-3.5 text-muted-foreground/50" />
                            <AddToPlaylist
                              track={{
                                trackId: track.id || track.workId || '',
                                title: track.title || '',
                                artist: track.artist?.name || track.artistName || artistName,
                                imageUrl: item?.coverArtUrl || item?.imageUrl || '',
                                audioUrl: track.audioUrl || '',
                                duration: track.durationMs ? Math.round(track.durationMs / 1000) : undefined,
                              }}
                              variant="icon"
                              className="h-7 w-7 bb-hover-reveal"
                            />
                          </>
                        )}
                        {/* Favorite this track */}
                        {(track.workId || track.id) && (
                          <SaveButton
                            itemId={track.workId || track.id || ''}
                            itemType="track"
                            title={track.title || ''}
                            subtitle={track.artist?.name || track.artistName || artistName}
                            imageUrl={item?.coverArtUrl || item?.imageUrl || ''}
                            audioUrl={track.audioUrl || ''}
                            link={`/music/${track.workId || track.id}`}
                            size="icon"
                            variant="ghost"
                            showLabel={false}
                            className="h-7 w-7 bb-hover-reveal"
                          />
                        )}
                        {/* Share this track — title leads with the artist so
                            the bold line in every share card / OS share sheet
                            shows the artist name first. */}
                        {(track.workId || track.id) && (
                          <ShareButton
                            title={(() => {
                              const trackArtist = (track.artist?.name || track.artistName || artistName || '').trim();
                              const useArtist = trackArtist && trackArtist !== t('unknownArtist');
                              return useArtist ? `${trackArtist} — ${track.title || ''}` : (track.title || '');
                            })()}
                            url={`/music/${track.workId || track.id}`}
                            variant="ghost"
                            size="icon"
                            iconOnly
                            className="h-7 w-7 bb-hover-reveal"
                          />
                        )}
                        {/* Per-track download (release view) — reuse the shared
                            PWYW DownloadButton so it opens the same Name-Your-
                            Price dialog as everywhere else, instead of jumping
                            straight to Stripe. Gordon: "the download icon ...
                            goes straight to stripe checkout ... no pwyw. all
                            three should have same pwyw window." */}
                        {(track.workId || track.audioUrl) && (
                          <div className="bb-hover-reveal" onClick={(e) => e.stopPropagation()}>
                            <DownloadButton
                              iconOnly
                              item={{
                                id: track.workId || track.id,
                                title: track.title || item.title,
                                artist: track.artist?.name || track.artistName || item.artist,
                                coverArtUrl: track.coverArtUrl || item.coverArtUrl,
                                downloadType: track.downloadType || 'pwyw',
                                downloadMinPrice: track.downloadMinPrice ?? item.downloadMinPrice ?? 0,
                                downloadCurrency: track.downloadCurrency || item.downloadCurrency || 'eur',
                                audioUrl: track.audioUrl || track.mp3Url,
                                hiResUrl: track.hiResUrl || track.wavUrl,
                                allowDownloads: true,
                              }}
                              tracks={[track]}
                            />
                          </div>
                        )}
                        {/* Open full song page */}
                        {(track.workId || track.id) && (
                          <Link
                            href={`/music/${track.workId || track.id}`}
                            onClick={e => e.stopPropagation()}
                          >
                            <Button variant="ghost" size="icon" className="h-7 w-7 bb-hover-reveal" title="Open full song page">
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Button>
                          </Link>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Inline lyrics view (read mode) */}
                  {!editMode && expandedLyricsTrackId === track.id && track.lyrics && (
                    <div className="ml-9 mr-2 mb-2 p-3 rounded-lg bg-secondary/30 text-sm whitespace-pre-line text-muted-foreground">
                      {track.lyrics}
                    </div>
                  )}

                  {/* Inline lyrics editor (edit mode) */}
                  {editMode && editingLyricsIdx === i && (
                    <div className="mx-2 mb-2 space-y-1.5">
                      <Textarea
                        value={track.lyrics || ''}
                        onChange={e => updateTrackLyrics(i, e.target.value)}
                        placeholder="Enter lyrics for this track…"
                        className="min-h-[120px] text-sm resize-y font-mono"
                      />
                    </div>
                  )}

                  {/* Inline audio URL editor (edit mode) */}
                  {editMode && editingAudioIdx === i && (
                    <div className="mx-2 mb-2 space-y-1.5" onClick={e => e.stopPropagation()}>
                      <p className="text-xs text-muted-foreground font-medium">Audio file URL</p>
                      <Input
                        value={track.audioUrl || ''}
                        onChange={e => updateTrackAudio(i, e.target.value)}
                        placeholder="https://firebasestorage.googleapis.com/… or any direct audio URL"
                        className="h-8 text-xs font-mono"
                      />
                      {!track.audioUrl && (
                        <p className="text-xs text-orange-500/80">No audio linked — paste a URL above, then Save Tracks.</p>
                      )}
                    </div>
                  )}

                  {/* Inline per-track videos editor (edit mode) — writes through
                      to works/{workId} immediately so the change is visible on
                      the track's own page without a release-level Save. */}
                  {editMode && editingVideosIdx === i && (
                    <div className="mx-2 mb-2 space-y-2" onClick={e => e.stopPropagation()}>
                      <p className="text-xs text-muted-foreground font-medium flex items-center gap-1.5">
                        <Video className="h-3 w-3" /> Videos for "{track.title || `Track ${i + 1}`}"
                      </p>
                      {/* Existing videos list */}
                      {[
                        ...(track.videoUrl ? [{ url: track.videoUrl as string, isPrimary: true }] : []),
                        ...((Array.isArray(track.videos) ? track.videos.filter(Boolean) : []) as string[])
                          .map((u: string) => ({ url: u, isPrimary: false })),
                      ].map(({ url, isPrimary }, vi) => {
                        const ytId = extractVideoId(url);
                        return (
                          <div key={`${url}-${vi}`} className="flex items-start gap-2 p-1.5 rounded-md bg-muted/40">
                            <div className="relative w-24 aspect-video rounded overflow-hidden bg-black flex-shrink-0">
                              {ytId ? (
                                <iframe
                                  src={`https://www.youtube.com/embed/${ytId}?modestbranding=1`}
                                  title={`Track ${i + 1} video ${vi + 1}`}
                                  className="absolute inset-0 w-full h-full pointer-events-none"
                                  allow="encrypted-media"
                                />
                              ) : (
                                <div className="absolute inset-0 flex items-center justify-center text-[10px] text-muted-foreground">Direct link</div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] truncate font-mono text-muted-foreground">{url}</p>
                              {isPrimary && <span className="text-[10px] text-primary font-medium">Primary</span>}
                            </div>
                            <Button
                              variant="ghost" size="icon" className="h-6 w-6 text-destructive flex-shrink-0"
                              onClick={() => removeTrackVideo(i, url)}
                              disabled={savingTrackVideo}
                              title="Remove video"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        );
                      })}
                      {/* Add new video URL */}
                      <div className="flex gap-1.5">
                        <Input
                          type="url"
                          placeholder="YouTube URL or direct video link"
                          value={editingVideosIdx === i ? trackVideoDraft : ''}
                          onChange={e => setTrackVideoDraft(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTrackVideo(i); } }}
                          className="h-8 text-xs flex-1"
                          disabled={savingTrackVideo}
                        />
                        <Button
                          size="sm"
                          onClick={() => addTrackVideo(i)}
                          disabled={!trackVideoDraft.trim() || savingTrackVideo}
                          className="h-8 gap-1.5 text-xs"
                        >
                          {savingTrackVideo ? <Loader2 className="h-3 w-3 animate-spin" /> : <PlusCircle className="h-3 w-3" />}
                          Add
                        </Button>
                      </div>
                      {!track.workId && !track.id && (
                        <p className="text-[10px] text-orange-500/80">This track has no workId — videos can't be persisted until it's linked.</p>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {/* Edit mode: Save + Add track */}
              {editMode && (
                <div className="pt-3 space-y-3">
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={saveTracks} disabled={isSavingTracks} className="h-8 text-xs gap-1.5">
                      <Check className="h-3.5 w-3.5" />
                      {isSavingTracks ? tCommon('saving') : 'Save track order & lyrics'}
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5"
                      onClick={() => setAddTrackExpanded(v => !v)}>
                      <Plus className="h-3.5 w-3.5" />
                      Link track from vault
                    </Button>
                    {/* Heal — for each track in the release, re-pull the audio
                        URL + title from the matching work doc and write them
                        back into release.tracks[]. Solves the "no music playing"
                        case where a release was edited before tracks finished
                        uploading, or audio was re-encoded after the release was
                        first saved. */}
                    <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5"
                      onClick={async () => {
                        try {
                          setIsSavingTracks(true);
                          const healed: any[] = [];
                          let fixed = 0;
                          for (const tk of localTracks) {
                            if (!tk.workId) { healed.push(tk); continue; }
                            try {
                              const snap = await getDoc(doc(db, 'works', tk.workId));
                              if (!snap.exists()) { healed.push(tk); continue; }
                              const w = snap.data() as any;
                              const audio = w.audioUrl || w.mp3Url || tk.audioUrl || tk.mp3Url;
                              const next = {
                                ...tk,
                                title: tk.title || w.title || '',
                                audioUrl: audio || tk.audioUrl,
                                mp3Url: audio || tk.mp3Url,
                                durationMs: tk.durationMs || (w.durationSeconds ? Math.round(w.durationSeconds * 1000) : tk.durationMs),
                              };
                              if (
                                next.audioUrl !== tk.audioUrl ||
                                next.mp3Url !== tk.mp3Url ||
                                next.title !== tk.title ||
                                next.durationMs !== tk.durationMs
                              ) fixed++;
                              healed.push(next);
                            } catch {
                              healed.push(tk);
                            }
                          }
                          setLocalTracks(healed);
                          await updateRelease(resolvedId, { tracks: healed });
                          alert(`Healed ${fixed} of ${localTracks.length} tracks. Reload the page to hear them.`);
                        } catch (err) {
                          console.error('Heal tracks failed', err);
                          alert(`Heal failed: ${(err as any)?.message || err}`);
                        } finally {
                          setIsSavingTracks(false);
                        }
                      }}
                      disabled={isSavingTracks || localTracks.length === 0}
                      title="Re-pull audio URL, title, and duration from each track's work doc and write back into this release. Use when a track shows but won't play."
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Heal tracks
                    </Button>
                  </div>

                  {/* Vault track search */}
                  {addTrackExpanded && (
                    <div className="p-3 rounded-lg border border-border/50 bg-secondary/20 space-y-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input type="search"
                          placeholder="Search works by title…"
                          className="pl-8 h-8 text-xs"
                          value={trackSearch}
                          onChange={e => setTrackSearch(e.target.value)}
                        />
                      </div>
                      {isSearchingTracks && <p className="text-xs text-muted-foreground text-center">Searching…</p>}
                      {trackSearchResults.length > 0 && (
                        <div className="space-y-0.5">
                          {trackSearchResults.map((work: any) => (
                            <button key={work.id}
                              className="w-full flex items-center gap-2 p-2 rounded-md hover:bg-secondary/60 text-left transition-colors"
                              onClick={() => addTrackFromVault(work)}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium truncate">{work.title}</p>
                                <p className="text-[10px] text-muted-foreground truncate">{work.artist || work.artistName || '—'}</p>
                              </div>
                              <Plus className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                            </button>
                          ))}
                        </div>
                      )}
                      {!isSearchingTracks && trackSearch && trackSearchResults.length === 0 && (
                        <p className="text-xs text-muted-foreground text-center py-2">No works found for "{trackSearch}"</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </TabsContent>
      )}

      {/* Lyrics tab — individual tracks only, not album-level */}
      <TabsContent value="lyrics" className="mt-4 space-y-4">
        <ExplicitGate
          explicit={!!(item as any).explicit && !canEdit}
          label="These lyrics are marked explicit."
          action="I'm 18+ — show lyrics"
        >
        {!isRelease ? (
          /* Individual track: full lyrics panel with editing */
          <MediaLyricsPanel
            mediaId={resolvedId}
            mediaTitle={item.title || ''}
            artistName={artistName}
            existingLyrics={item?.lyrics || ''}
            audioUrl={item?.audioUrl || (item as any)?.mp3Url || (item as any)?.hiResUrl || item?.url || ''}
            existingBpm={item?.bpm ?? null}
            existingKey={item?.key ?? null}
          />
        ) : localTracks.length === 1 ? (
          /* Single-track release: full lyrics panel for that one track — gives
             the "Transcribe from Audio" (generate lyrics) button + editing. */
          (() => {
            const tk: any = localTracks[0] || {};
            return (
              <MediaLyricsPanel
                mediaId={tk.workId || tk.id || resolvedId}
                mediaTitle={tk.title || item.title || ''}
                artistName={artistName}
                existingLyrics={tk.lyrics || ''}
                audioUrl={tk.audioUrl || tk.mp3Url || tk.hiResUrl || tk.url || item?.audioUrl || (item as any)?.mp3Url || item?.url || ''}
                existingBpm={tk.bpm ?? null}
                existingKey={tk.key ?? null}
              />
            );
          })()
        ) : (
          /* Multi-track release: a lyrics panel per track. Admins/creators get
             "Transcribe from Audio" + edit on each; listeners see the lyrics. */
          <div className="space-y-6">
            {localTracks.map((track: any, i: number) => (
              <div key={track.id || track.workId || i} className="space-y-2">
                <h3 className="text-sm font-semibold text-primary">
                  {i + 1}. {track.title}
                </h3>
                <MediaLyricsPanel
                  mediaId={track.workId || track.id || `${resolvedId}-t${i}`}
                  mediaTitle={track.title || item.title || ''}
                  artistName={artistName}
                  existingLyrics={track.lyrics || ''}
                  audioUrl={track.audioUrl || track.mp3Url || track.hiResUrl || track.url || ''}
                  existingBpm={track.bpm ?? null}
                  existingKey={track.key ?? null}
                />
              </div>
            ))}
            {localTracks.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No tracks in this release yet.</p>
              </div>
            )}
          </div>
        )}
        </ExplicitGate>
      </TabsContent>

      {/* Credits tab */}
      <TabsContent value="credits" className="mt-4 space-y-4">
        {/* Edit mode: credits list with delete + add form */}
        {editMode ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              {localCredits.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-3">No credits added yet</p>
              )}
              {localCredits.map((credit, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-secondary/30 border border-border/30">
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-muted-foreground">{credit.role}: </span>
                    <span className="text-sm font-medium">{credit.name}</span>
                  </div>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive/60 hover:text-destructive flex-shrink-0"
                    onClick={() => removeCredit(idx)} disabled={isSavingCredits}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Add new credit */}
            {/* DB-driven: Role autocompletes from the shared role library; Name
                searches the users database so credits link to real accounts and
                don't fork duplicates. */}
            <div className="flex items-center gap-2 p-3 rounded-lg border border-dashed border-border/60 bg-secondary/10">
              <div className="flex-1"><RoleAutocomplete value={newCreditRole} onChange={setNewCreditRole} placeholder="Role (e.g. Producer)" className="h-8 text-xs" /></div>
              <div className="flex-1"><UserSearchInput value={newCreditName} placeholder="Search users by name or email…"
                onPick={(u) => { setNewCreditName(u.displayName || u.email || ''); setNewCreditUserId(u.uid); }}
                onChangeText={(raw) => { setNewCreditName(raw); setNewCreditUserId(''); }} /></div>
              <Button size="icon" className="h-8 w-8 flex-shrink-0" onClick={addCredit} disabled={!newCreditRole.trim() || !newCreditName.trim() || isSavingCredits}>
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
            {isSavingCredits && <p className="text-xs text-muted-foreground text-center">{tCommon('saving')}</p>}
          </div>
        ) : isRelease ? (
          /* A release: liner notes — whole album, then track by track. */
          <AlbumCreditSheet
            album={localCredits}
            tracks={[...(tracks || [])]
              .sort((a: any, b: any) => (a.trackNumber || 999) - (b.trackNumber || 999))
              .map((tr: any, i: number) => ({
                n: tr.trackNumber || i + 1,
                title: tr.title || "",
                href: `/music/${tr.workId || tr.id}`,
                credits: normalizeCredits(tr.credits),
              }))}
          />
        ) : (
          /* View mode: grouped display */
          localCredits.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(() => {
                // Group localCredits by role for display
                const grouped: Record<string, typeof localCredits> = {};
                localCredits.forEach(c => { if (!grouped[c.role]) grouped[c.role] = []; grouped[c.role].push(c); });
                return Object.entries(grouped).map(([role, people]) => (
                  <div key={role} className="text-sm">
                    <span className="text-muted-foreground">{role}:</span>{' '}
                    <span className="font-medium">
                      {people.map((credit, idx) => (
                        <React.Fragment key={credit.name}>
                          {idx > 0 && ', '}
                          <CreditLink name={credit.name} href={credit.href} role={role} />
                        </React.Fragment>
                      ))}
                    </span>
                  </div>
                ));
              })()}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No credits available</p>
          )
        )}

        {/* Participant Rights Panel — three-part rights (Writing / Publishing / Performance) */}
        <div className="pt-4 border-t border-border/50">
          <ParticipantPanel
            mediaId={resolvedId}
            mediaType={isRelease ? 'release' : 'work'}
            mediaTitle={item.title || ''}
            mediaCoverUrl={coverUrl}
            tracks={isRelease
              ? (tracks || [])
                  .map((t: any) => ({
                    workId: t.workId || t.id,
                    title: t.title || "Untitled",
                    trackNumber: t.trackNumber,
                    audioUrl: t.audioUrl || t.mp3Url,
                    mp3Url: t.mp3Url,
                    imageUrl: t.imageUrl || coverUrl,
                    artist: typeof item.artist === 'string' ? item.artist : item.artist?.name || '',
                  }))
                  .filter((t: any) => t.workId)
              : undefined}
          />
        </div>

        {/* Claim Credits & Splits */}
        <div className="pt-4 border-t border-border/50">
          <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wider">Credit Claims & Splits</h3>
          <MediaCreditsPanel
            mediaId={resolvedId}
            mediaType={isRelease ? 'release' : 'work'}
            mediaTitle={item.title || ''}
            tracks={isRelease
              ? (tracks || [])
                  .map((t: any) => ({
                    workId: t.workId || t.id,
                    title: t.title || "Untitled",
                    trackNumber: t.trackNumber,
                    audioUrl: t.audioUrl || t.mp3Url,
                    mp3Url: t.mp3Url,
                  }))
                  .filter((t: any) => t.workId)
              : undefined}
          />
        </div>
      </TabsContent>

      {/* ── Analysis tab ─────────────────────────────────────────────────── */}
      <TabsContent value="analysis" className="mt-4 space-y-4">
        <MusicAnalysisPanel
          initialAnalysis={(item as any).musicAnalysis ?? null}
          audioUrl={
            isRelease
              ? (tracks.map((t: any) => t.audioUrl || t.mp3Url).find(Boolean) ?? "")
              : ((item as any).audioUrl || (item as any).mp3Url || "")
          }
          workId={resolvedId}
          isRelease={isRelease}
          title={item.title}
          artist={artistName}
          canAnalyze={canEdit}
        />
        {/* Score & MIDI — in-browser note detection → MIDI + engraved sheet music */}
        <ScoreTranscriber
          audioUrl={
            isRelease
              ? (tracks.map((t: any) => t.audioUrl || t.mp3Url).find(Boolean) ?? "")
              : ((item as any).audioUrl || (item as any).mp3Url || "")
          }
          title={item.title}
          artist={artistName}
          bpm={(item as any).bpm ?? (item as any).musicAnalysis?.bpm ?? null}
          keyName={(item as any).key ?? (item as any).musicAnalysis?.keyName ?? null}
        />
      </TabsContent>

      {/* Comments tab — SoundCloud-style timeline comments */}
      <TabsContent value="comments" className="mt-4">
        <div className="flex flex-col gap-4">
          {/* Comment input */}
          {user ? (
            <div className="space-y-2 p-3 rounded-lg border border-border/60 bg-secondary/20">
              <Textarea
                placeholder="Add a comment…"
                value={newCommentText}
                onChange={(e) => setNewCommentText(e.target.value)}
                className="min-h-[72px] resize-none text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    handleSubmitComment();
                  }
                }}
              />
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => setPinToTime(v => !v)}
                  className={cn(
                    "flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border transition-colors",
                    pinToTime
                      ? "border-foreground bg-foreground text-background"
                      : "border-border text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Timer className="h-3 w-3" />
                  {pinToTime ? "Pinned to current time" : "Pin to time"}
                </button>
                <Button
                  size="sm"
                  onClick={handleSubmitComment}
                  disabled={!newCommentText.trim() || isSubmittingComment}
                  className="h-7 text-xs gap-1"
                >
                  <Send className="h-3 w-3" />
                  Post
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-6">
              <p className="text-sm text-muted-foreground">Sign in to leave a comment</p>
              <Link
                href="/signin"
                className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
              >
                Sign In
              </Link>
            </div>
          )}

          {/* Comments list */}
          {comments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">{t('noComments')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Pinned/timestamped comments first, then global */}
              {[...comments].sort((a, b) => {
                if (!a.isGlobal && b.isGlobal) return -1;
                if (a.isGlobal && !b.isGlobal) return 1;
                return (a.timestampSeconds || 0) - (b.timestampSeconds || 0);
              }).map((comment) => (
                <div key={comment.id} className="flex items-start gap-2.5 group">
                  <Avatar className="h-7 w-7 flex-shrink-0 mt-0.5">
                    <AvatarImage src={comment.userImageUrl || ''} />
                    <AvatarFallback className="text-[10px]">
                      {comment.userName?.slice(0, 2).toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-medium leading-none">{comment.userName}</span>
                      {!comment.isGlobal && comment.timestampSeconds > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary font-mono font-semibold">
                          <Timer className="h-2.5 w-2.5" />
                          {formatCommentTimestamp(comment.timestampSeconds)}
                        </span>
                      )}
                      {comment.isGlobal && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">
                          <Globe className="h-2.5 w-2.5" />
                          general
                        </span>
                      )}
                      <span className="text-[10px] text-muted-foreground ml-auto">{formatTimeAgo(comment.createdAt)}</span>
                    </div>
                    <p className="text-sm text-foreground/90 mt-1 leading-snug">{comment.content}</p>
                    <CommentReactions
                      reactions={comment.reactions}
                      currentUserId={user?.uid}
                      onToggle={(emoji, hasReacted) => handleCommentReaction(comment.id, emoji, hasReacted)}
                      className="mt-1.5"
                    />
                  </div>
                  {user?.uid === comment.userId && (
                    <button
                      onClick={() => handleDeleteComment(comment.id)}
                      className="bb-hover-reveal transition-opacity p-1 rounded hover:text-destructive text-muted-foreground"
                      title="Delete comment"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </TabsContent>

      {/* Web Info tab */}
      <TabsContent value="web" className="mt-4">
        <WebResultsSection
          query={`${item.title || ''} ${artistName}`}
          entityType={isRelease ? "release" : "track"}
          entityName={item.title || artistName}
          entityId={resolvedId}
          // Tracks are stored in `works`, releases in `releases`. Without this
          // conditional, admin dismissals + curated links on a track page were
          // being written to the wrong collection — so erases never stuck.
          entityCollection={item.type === "work" ? "works" : "releases"}
          canManage={canEdit || (!!user?.uid && [(item as any)?.uploadedBy, (item as any)?.createdBy, (item as any)?.userId, (item as any)?.owner?.userId].includes(user.uid))}
        />
      </TabsContent>
    </Tabs>
    </div>
  );
}
